import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  collection, 
  addDoc, 
  getDocs, 
  deleteDoc, 
  doc, 
  updateDoc,
  query,
  orderBy,
  onSnapshot,
  serverTimestamp,
  where
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { 
  db, 
  storage, 
  getSystemSettings, 
  updateSystemSettings, 
  testFirebaseConnection,
  getAdminUsers,
  addAdminUser,
  updateAdminUser,
  deleteAdminUser,
  validateAdminLogin,
  createPasswordResetRequest,
  sendPasswordResetEmail,
  validatePasswordResetToken,
  resetAdminPassword,
  cleanupExpiredResetTokens,
  debugAdminUsers,
  forceInitializeAdminUser,
  logEvent,
  getEventLogs,
  deleteOldEventLogs,
  getEventLogsSummary
} from '../../services/firebase';
import { runDataMigration } from '../../utils/migrateEventLogs';
import activityLogger, { logAdminActivity, logUIChange, logSystemActivity, logAppError } from '../../utils/activityLogger';
import useActivityLogger from '../../hooks/useActivityLogger';
import { getAllUsers } from '../../services/authService';
import toast from 'react-hot-toast';
import { 
  Plus, 
  Trash2, 
  Edit3, 
  Save, 
  X, 
  Search, 
  Filter,
  BarChart3,
  MessageSquare,
  Users,
  Settings,
  Upload,
  Link,
  FileText,
  Palette,
  Globe,
  Moon,
  Sun,
  Database,
  Key,
  Eye,
  EyeOff,
  Wifi,
  WifiOff,
  Download,
  Sparkles,
  RefreshCw,
  Mail,
  ArrowLeft,
  Clock
} from 'lucide-react';

const AdminApp = () => {
  const { t } = useTranslation();
  
  // Activity logging for admin panel
  const { 
    logActivity, 
    logUserAction, 
    logStateChange, 
    logError, 
    logButtonClick,
    logFormSubmit,
    logNavigation,
    logModalOpen,
    logModalClose,
    logDataLoad 
  } = useActivityLogger('AdminApp', { version: '1.0', userType: 'admin' });
  
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [currentAdmin, setCurrentAdmin] = useState(null);
  const [activeTab, setActiveTab] = useState('content');
  const [firebaseConnected, setFirebaseConnected] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  
  // Password reset states
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showResetForm, setShowResetForm] = useState(false);
  const [resetLink, setResetLink] = useState('');
  
  // Data states
  const [content, setContent] = useState([]);
  const [chats, setChats] = useState([]);
  const [leads, setLeads] = useState([]);
  const [jobApplications, setJobApplications] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);
  const [users, setUsers] = useState([]);
  
  // UI states
  const [loading, setLoading] = useState(false);
  const [contentLoading, setContentLoading] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [cmsLoading, setCmsLoading] = useState(false);
  const [adminLoading, setAdminLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [showAddContent, setShowAddContent] = useState(false);
  const [editingContent, setEditingContent] = useState(null);
  const [showAddAdmin, setShowAddAdmin] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [showEditAdmin, setShowEditAdmin] = useState(false);
  const [adminSearchTerm, setAdminSearchTerm] = useState('');
  const [adminRoleFilter, setAdminRoleFilter] = useState('all');
  const [newAdmin, setNewAdmin] = useState({
    name: '',
    email: '',
    password: '',
    role: 'admin'
  });
  const [newContent, setNewContent] = useState({
    subject: '',
    category: '',
    details: '',
    keywords: '',
    active: true,
    attachments: []
  });

  // AI-powered content features
  const [keywordLoading, setKeywordLoading] = useState(false);
  const [documentAnalysisLoading, setDocumentAnalysisLoading] = useState(false);
  const [analyzedContent, setAnalyzedContent] = useState(null);

  // System Settings
  const [systemSettings, setSystemSettings] = useState({
    theme: {
      primaryColor: '#ce6161',
      secondaryColor: '#000000',
      darkMode: false
    },
    branding: {
      logoUrl: '/scalovate_logo.png',
      darkLogoUrl: '',
      headerText: 'Paradigm Shift in Technology',
      footerText: 'Scalovate - AI Solutions & Enterprise Intelligence',
      copyright: '© 2025 Scalovate. All rights reserved.',
      locations: 'Qatar • Australia • Canada',
      headerBackgroundColor: 'linear-gradient(to right, rgba(255, 255, 255, 0), rgba(200, 200, 200, 0.5))',
      headerTextColor: '#1f2937',
      headerTransparency: 90,
      chatHeaderBackgroundColor: 'linear-gradient(to right, rgba(255, 255, 255, 0), rgba(200, 200, 200, 0.5))',
      chatHeaderTextColor: '#1f2937',
      chatHeaderTransparency: 85,
      footerBackgroundColor: 'linear-gradient(to right, rgba(255, 255, 255, 0), rgba(200, 200, 200, 0.5))',
      footerTextColor: '#1f2937',
      footerTransparency: 95,
      navigationLinks: [
        { name: 'Home', url: '/', active: true },
        { name: 'Services', url: '/services', active: true },
        { name: 'About', url: '/about', active: true },
        { name: 'Contact', url: '/contact', active: true }
      ],
      socialMediaLinks: [
        { name: 'LinkedIn', url: 'https://linkedin.com/company/scalovate', icon: 'linkedin', active: true },
        { name: 'YouTube', url: 'https://youtube.com/@scalovate', icon: 'youtube', active: true },
        { name: 'Facebook', url: 'https://facebook.com/scalovate', icon: 'facebook', active: true },
        { name: 'Instagram', url: 'https://instagram.com/scalovate', icon: 'instagram', active: true },
        { name: 'Twitter', url: 'https://twitter.com/scalovate', icon: 'twitter', active: false }
      ]
    },
    aiAssistant: {
      name: 'Jeje',
      avatarUrl: '/jeje_bot.png',
      description: 'AI Assistant from Scalovate',
      personality: 'Professional and helpful AI assistant specializing in technology solutions',
      welcomeMessage: 'Welcome to Scalovate! How can I assist you today?',
      communicationGuidelines: 'Be polite, helpful, and knowledgeable. Answer questions clearly and concisely. Use technical jargon when appropriate.',
      responseMaturity: 'generic'
    },
    aiModels: {
      ollama: { enabled: true, apiUrl: 'http://localhost:11434' },
      openai: { enabled: false, apiKey: '' },
      groq: { enabled: false, apiKey: '' },
      claude: { enabled: false, apiKey: '' },
      gemini: { enabled: false, apiKey: '' }
    },
    actionButtons: [
      { text: 'AI Solutions', key: 'ai-solutions', active: true },
      { text: 'Products', key: 'products', active: true },
      { text: 'About', key: 'about', active: true },
      { text: 'PrivateAI', key: 'private-ai', active: true },
      { text: 'AI Usecases', key: 'ai-usecases', active: true },
      { text: 'Contact', key: 'contact', active: true }
    ]
  });
  
  const [showApiKeys, setShowApiKeys] = useState({});

  // Event Logs and Reports states
  const [eventLogs, setEventLogs] = useState([]);
  const [eventLogsSummary, setEventLogsSummary] = useState({});
  const [logsLoading, setLogsLoading] = useState(false);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [logsDaysFilter, setLogsDaysFilter] = useState(30);
  const [reportsDaysFilter, setReportsDaysFilter] = useState(30);
  const [migrationLoading, setMigrationLoading] = useState(false);
  const [testLogsLoading, setTestLogsLoading] = useState(false);

  // Initialize Firebase connection
  useEffect(() => {
    const initializeFirebase = async () => {
      console.log('🔧 Testing Firebase connection for Admin...');
      
      const connected = await testFirebaseConnection();
      setFirebaseConnected(connected);
      
      if (connected) {
        toast.success('🔥 Firebase connected successfully!');
      } else {
        toast.error('❌ Firebase connection failed');
      }
    };

    initializeFirebase();
  }, []);

  // Load data when logged in and Firebase connected
  useEffect(() => {
    if (isLoggedIn && firebaseConnected) {
      loadAllData();
      setupRealtimeListeners();
    }
  }, [isLoggedIn, firebaseConnected]);

  // Setup real-time listeners
  const setupRealtimeListeners = () => {
    try {
      // Content listener
      const contentQuery = query(collection(db, 'content'), orderBy('createdAt', 'desc'));
      const unsubscribeContent = onSnapshot(contentQuery, (snapshot) => {
        const contentData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setContent(contentData);
      });

      // Chats listener
      const chatsQuery = query(collection(db, 'chats'), orderBy('timestamp', 'desc'));
      const unsubscribeChats = onSnapshot(chatsQuery, (snapshot) => {
        const chatsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setChats(chatsData);
      });

      // Leads listener
      const leadsQuery = query(collection(db, 'leads'), orderBy('createdAt', 'desc'));
      const unsubscribeLeads = onSnapshot(leadsQuery, (snapshot) => {
        const leadsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setLeads(leadsData);
      });

      // Job Applications listener
      const jobsQuery = query(collection(db, 'jobApplications'), orderBy('createdAt', 'desc'));
      const unsubscribeJobs = onSnapshot(jobsQuery, (snapshot) => {
        const jobsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setJobApplications(jobsData);
      });

      return () => {
        unsubscribeContent();
        unsubscribeChats();
        unsubscribeLeads();
        unsubscribeJobs();
      };
    } catch (error) {
      console.error('Error setting up listeners:', error);
    }
  };

  // Load all data
  const loadAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadContent(),
        loadChats(),
        loadLeads(),
        loadJobApplications(),
        loadAdminUsers(),
        loadUsers()
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Load functions
  const loadContent = async () => {
    try {
      const snapshot = await getDocs(collection(db, 'content'));
      const contentData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setContent(contentData);
    } catch (error) {
      console.error('Error loading content:', error);
    }
  };

  const loadChats = async () => {
    try {
      console.log('🔍 Loading chat logs...');
      
      // Try with orderBy first, fallback to simple query if it fails
      let snapshot;
      try {
        const q = query(collection(db, 'chats'), orderBy('timestamp', 'desc'));
        snapshot = await getDocs(q);
      } catch (orderError) {
        console.log('⚠️ OrderBy failed for chats, trying simple query:', orderError.message);
        // Fallback to simple query without orderBy
        snapshot = await getDocs(collection(db, 'chats'));
      }
      
      const chatsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      console.log('✅ Successfully loaded chats:', chatsData.length);
      setChats(chatsData);
    } catch (error) {
      console.error('❌ Error loading chats:', error);
      setChats([]); // Set empty array on error
    }
  };

  const loadLeads = async () => {
    try {
      const q = query(collection(db, 'leads'), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      const leadsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setLeads(leadsData);
    } catch (error) {
      console.error('Error loading leads:', error);
    }
  };

  const loadJobApplications = async () => {
    try {
      const q = query(collection(db, 'jobApplications'), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      const jobsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setJobApplications(jobsData);
    } catch (error) {
      console.error('Error loading job applications:', error);
    }
  };

  // Load admin users
  const loadAdminUsers = async () => {
    try {
      const users = await getAdminUsers();
      setAdminUsers(users);
    } catch (error) {
      console.error('Error loading admin users:', error);
    }
  };

  // Load users
  const loadUsers = async () => {
    try {
      console.log('🔍 Loading registered users...');
      const users = await getAllUsers();
      console.log('📊 Found users:', users.length, users);
      setUsers(users);
      
      // If no users found, try direct Firestore query as fallback
      if (users.length === 0) {
        console.log('⚠️ No users found via getAllUsers, trying direct query...');
        try {
          const snapshot = await getDocs(collection(db, 'users'));
          const directUsers = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          console.log('📊 Direct query found users:', directUsers.length, directUsers);
          setUsers(directUsers);
        } catch (directError) {
          console.error('❌ Direct query also failed:', directError);
        }
      }
    } catch (error) {
      console.error('❌ Error loading users:', error);
      setUsers([]); // Set empty array on error
    }
  };

  // Handle login
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Log login attempt
      await logUserAction('login_attempt', `Admin login attempt for: ${loginData.email}`, { 
        email: loginData.email,
        timestamp: new Date().toISOString()
      });
      
      const result = await validateAdminLogin(loginData.email, loginData.password);
      
      if (result.success) {
        setIsLoggedIn(true);
        setCurrentAdmin(result.admin);
        
        // Enhanced logging with activity logger
        await logUserAction('login_success', `Admin logged in: ${result.admin.email} (${result.admin.role})`, {
          adminId: result.admin.id,
          adminEmail: result.admin.email,
          adminRole: result.admin.role,
          adminName: result.admin.name,
          loginTime: new Date().toISOString(),
          sessionStart: true
        });
        
        // Log the login event to Firebase
        await logEvent(
          'authentication',
          'admin_login',
          `Admin user ${result.admin.name} logged in successfully`,
          {
            adminRole: result.admin.role,
            loginTime: new Date().toISOString(),
            sessionStart: true
          },
          result.admin.id,
          result.admin.email
        );
        
        toast.success(`Welcome back, ${result.admin.name}!`);
      } else {
        // Log failed login
        await logUserAction('login_failed', `Admin login failed for: ${loginData.email}`, {
          email: loginData.email,
          error: result.message,
          timestamp: new Date().toISOString()
        });
        
        toast.error(result.message || 'Invalid credentials');
      }
    } catch (error) {
      console.error('Login error:', error);
      
      // Log login error
      await logError(error, 'handleLogin', { 
        email: loginData.email,
        errorType: 'login_exception'
      });
      
      toast.error('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Content management functions
  const handleAddContent = async () => {
    if (!newContent.subject || !newContent.details) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      const docRef = await addDoc(collection(db, 'content'), {
        ...newContent,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      // Log the content creation event
      await logEvent(
        'content',
        'content_create',
        `Created new content: ${newContent.subject}`,
        { 
          contentId: docRef.id, 
          category: newContent.category,
          adminRole: currentAdmin?.role
        },
        currentAdmin?.id,
        currentAdmin?.email
      );
      
      setNewContent({
        subject: '',
        category: '',
        details: '',
        keywords: '',
        active: true,
        attachments: []
      });
      setShowAddContent(false);
      toast.success('Content added successfully!');
    } catch (error) {
      console.error('Error adding content:', error);
      toast.error('Failed to add content');
    }
  };

  const handleUpdateContent = async () => {
    if (!editingContent) return;

    try {
      await updateDoc(doc(db, 'content', editingContent.id), {
        ...editingContent,
        updatedAt: serverTimestamp()
      });
      
      // Log the content update event
      await logEvent(
        'content',
        'content_update',
        `Updated content: ${editingContent.subject}`,
        { 
          contentId: editingContent.id, 
          category: editingContent.category,
          adminRole: currentAdmin?.role
        },
        currentAdmin?.id,
        currentAdmin?.email
      );
      
      setEditingContent(null);
      toast.success('Content updated successfully!');
    } catch (error) {
      console.error('Error updating content:', error);
      toast.error('Failed to update content');
    }
  };

  const handleDeleteContent = async (id) => {
    if (window.confirm('Are you sure you want to delete this content?')) {
      try {
        // Get content info before deletion for logging
        const contentToDelete = content.find(c => c.id === id);
        
        await deleteDoc(doc(db, 'content', id));
        
        // Log the content deletion event
        await logEvent(
          'content',
          'content_delete',
          `Deleted content: ${contentToDelete?.subject || 'Unknown'}`,
          { 
            contentId: id, 
            category: contentToDelete?.category,
            adminRole: currentAdmin?.role
          },
          currentAdmin?.id,
          currentAdmin?.email
        );
        
        toast.success('Content deleted successfully!');
      } catch (error) {
        console.error('Error deleting content:', error);
        toast.error('Failed to delete content');
      }
    }
  };

  // Export to CSV function
  const exportToCSV = (data, filename) => {
    if (!data.length) {
      toast.error('No data to export');
      return;
    }

    const headers = Object.keys(data[0]).filter(key => key !== 'id' && key !== 'attachments');
    const csvContent = [
      headers.join(','),
      ...data.map(row => 
        headers.map(header => {
          const value = row[header];
          if (value && typeof value === 'object' && value.toDate) {
            return `"${value.toDate().toLocaleString()}"`;
          }
          return `"${String(value || '').replace(/"/g, '""')}"`;
        }).join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('✅ Data exported successfully!');
  };

  // Bulk export all data
  const exportAllData = async () => {
    try {
      setLoading(true);
      
      // Create a comprehensive export
      const exportData = {
        content: content,
        leads: leads,
        jobApplications: jobApplications,
        chats: chats.slice(0, 100), // Limit chats to last 100
        systemSettings: systemSettings,
        exportDate: new Date().toISOString(),
        totalRecords: {
          content: content.length,
          leads: leads.length,
          jobApplications: jobApplications.length,
          chats: chats.length
        }
      };
      
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `scalovate_backup_${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      window.URL.revokeObjectURL(url);
      
      toast.success('✅ Complete backup exported successfully!');
    } catch (error) {
      console.error('Error exporting data:', error);
      toast.error('❌ Failed to export data');
    } finally {
      setLoading(false);
    }
  };

  // Filter data based on search term
  const filteredContent = content.filter(item => {
    const matchesSearch = item.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.keywords?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.details?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = filterCategory === 'all' || item.category === filterCategory;
    return matchesSearch && matchesCategory;
  });

  const filteredChats = chats.filter(chat =>
    (typeof chat.userMessage === 'string' && chat.userMessage.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (typeof chat.aiResponse === 'string' && chat.aiResponse.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (typeof chat.aiMessage === 'string' && chat.aiMessage.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const filteredLeads = leads.filter(lead =>
    lead.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    lead.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    lead.company?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredJobApplications = jobApplications.filter(job =>
    job.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    job.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    job.position?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calculate stats
  const stats = {
    totalContent: content.length,
    activeContent: content.filter(c => c.active).length,
    totalChats: chats.length,
    todayChats: chats.filter(c => {
      if (!c.timestamp?.toDate) return false;
      const today = new Date();
      const chatDate = c.timestamp.toDate();
      return chatDate.toDateString() === today.toDateString();
    }).length,
    totalLeads: leads.length,
    totalJobApplications: jobApplications.length
  };

  const handleFileUpload = async (file, contentId) => {
    try {
      const fileRef = ref(storage, `content/${contentId}/${file.name}`);
      const snapshot = await uploadBytes(fileRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      return {
        name: file.name,
        url: downloadURL,
        type: file.type,
        size: file.size
      };
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Failed to upload file');
      return null;
    }
  };

  const saveContent = async () => {
    if (!newContent.subject || !newContent.details) {
      toast.error('Please fill in Subject and Details fields');
      return;
    }

    try {
      setContentLoading(true);
      console.log('Saving content:', newContent);
      
      const contentData = {
        subject: newContent.subject,
        category: newContent.category || 'general',
        details: newContent.details,
        keywords: newContent.keywords || '',
        active: newContent.active,
        attachments: newContent.attachments || [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      
      console.log('Content data to save:', contentData);
      
      const docRef = await addDoc(collection(db, 'content'), contentData);
      console.log('Document written with ID: ', docRef.id);
      
      toast.success('✅ Content saved successfully to Firebase!');
      
      // Reset form
      setNewContent({
        subject: '',
        category: 'general',
        details: '',
        keywords: '',
        active: true,
        attachments: []
      });
      
      // Reload data to show new content
      await loadAllData();
      
    } catch (error) {
      console.error('Error saving content:', error);
      toast.error(`❌ Failed to save content: ${error.message}`);
    } finally {
      setContentLoading(false);
    }
  };

  const updateContent = async (id, updatedData) => {
    try {
      setLoading(true);
      console.log('Updating content:', id, updatedData);
      
      const updateData = {
        subject: updatedData.subject,
        category: updatedData.category,
        details: updatedData.details,
        keywords: updatedData.keywords,
        active: updatedData.active,
        updatedAt: serverTimestamp()
      };
      
      await updateDoc(doc(db, 'content', id), updateData);
      console.log('Content updated successfully');
      
      toast.success('Content updated successfully!');
      setEditingContent(null);
      await loadAllData();
      
    } catch (error) {
      console.error('Error updating content:', error);
      toast.error(`Failed to update content: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const deleteContent = async (id) => {
    if (window.confirm('Are you sure you want to delete this content?')) {
      try {
        await deleteDoc(doc(db, 'content', id));
        toast.success('Content deleted successfully');
        loadAllData();
      } catch (error) {
        console.error('Error deleting content:', error);
        toast.error('Failed to delete content');
      }
    }
  };

  const saveSystemSettings = async () => {
    try {
      setSettingsLoading(true);
      console.log('Saving system settings:', systemSettings);
      
      const success = await updateSystemSettings(systemSettings);
      
      if (success) {
        toast.success('✅ System settings saved successfully to Firebase!');
        console.log('System settings saved successfully');
      } else {
        throw new Error('Failed to save settings to Firebase');
      }
      
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error(`❌ Failed to save settings: ${error.message}`);
    } finally {
      setSettingsLoading(false);
    }
  };

  const toggleApiKeyVisibility = (model) => {
    setShowApiKeys(prev => ({
      ...prev,
      [model]: !prev[model]
    }));
  };

  // File upload functions
  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      toast.error('File size must be less than 5MB');
      return;
    }

    try {
      setLoading(true);
      console.log('🔄 Starting logo upload to Firebase Storage...');
      
      if (!storage) {
        throw new Error('Firebase Storage not initialized');
      }
      
      const storageRef = ref(storage, `branding/logo_${Date.now()}_${file.name}`);
      console.log('📁 Storage reference created:', storageRef.fullPath);
      
      const snapshot = await uploadBytes(storageRef, file);
      console.log('✅ File uploaded to Firebase Storage');
      
      const downloadURL = await getDownloadURL(snapshot.ref);
      console.log('🔗 Download URL obtained:', downloadURL);
      
      // Update local state
      const updatedSettings = {
        ...systemSettings,
        branding: { ...systemSettings.branding, logoUrl: downloadURL }
      };
      setSystemSettings(updatedSettings);
      
      // Save to Firebase immediately
      console.log('💾 Saving settings to Firebase...');
      const success = await updateSystemSettings(updatedSettings);
      
      if (success) {
        toast.success('✅ Logo uploaded and saved successfully!');
        console.log('✅ Logo settings saved to Firebase');
      } else {
        throw new Error('Failed to save logo settings to Firebase');
      }
    } catch (error) {
      console.error('❌ Error uploading logo:', error);
      
      if (error.code === 'storage/unauthorized') {
        toast.error('❌ Storage permission denied. Please check Firebase Storage rules.');
      } else if (error.code === 'storage/unknown') {
        toast.error('❌ Storage error. Please check your internet connection.');
      } else {
        toast.error(`❌ Failed to upload logo: ${error.message}`);
      }
    } finally {
      setLoading(false);
      // Clear the file input
      event.target.value = '';
    }
  };

  const handleAvatarUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      toast.error('File size must be less than 5MB');
      return;
    }

    try {
      setLoading(true);
      console.log('🔄 Starting avatar upload to Firebase Storage...');
      
      if (!storage) {
        throw new Error('Firebase Storage not initialized');
      }
      
      const storageRef = ref(storage, `avatars/avatar_${Date.now()}_${file.name}`);
      console.log('📁 Storage reference created:', storageRef.fullPath);
      
      const snapshot = await uploadBytes(storageRef, file);
      console.log('✅ File uploaded to Firebase Storage');
      
      const downloadURL = await getDownloadURL(snapshot.ref);
      console.log('🔗 Download URL obtained:', downloadURL);
      
      // Update local state
      const updatedSettings = {
        ...systemSettings,
        aiAssistant: { ...systemSettings.aiAssistant, avatarUrl: downloadURL }
      };
      setSystemSettings(updatedSettings);
      
      // Save to Firebase immediately
      console.log('💾 Saving settings to Firebase...');
      const success = await updateSystemSettings(updatedSettings);
      
      if (success) {
        toast.success('✅ Avatar uploaded and saved successfully!');
        console.log('✅ Avatar settings saved to Firebase');
      } else {
        throw new Error('Failed to save avatar settings to Firebase');
      }
    } catch (error) {
      console.error('❌ Error uploading avatar:', error);
      
      if (error.code === 'storage/unauthorized') {
        toast.error('❌ Storage permission denied. Please check Firebase Storage rules.');
      } else if (error.code === 'storage/unknown') {
        toast.error('❌ Storage error. Please check your internet connection.');
      } else {
        toast.error(`❌ Failed to upload avatar: ${error.message}`);
      }
    } finally {
      setLoading(false);
      // Clear the file input
      event.target.value = '';
    }
  };

  const handleDarkLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      toast.error('File size must be less than 5MB');
      return;
    }

    try {
      setLoading(true);
      console.log('🔄 Starting dark logo upload to Firebase Storage...');
      
      if (!storage) {
        throw new Error('Firebase Storage not initialized');
      }
      
      const storageRef = ref(storage, `branding/dark_logo_${Date.now()}_${file.name}`);
      console.log('📁 Storage reference created:', storageRef.fullPath);
      
      const snapshot = await uploadBytes(storageRef, file);
      console.log('✅ File uploaded to Firebase Storage');
      
      const downloadURL = await getDownloadURL(snapshot.ref);
      console.log('🔗 Download URL obtained:', downloadURL);
      
      // Update local state
      const updatedSettings = {
        ...systemSettings,
        branding: { ...systemSettings.branding, darkLogoUrl: downloadURL }
      };
      setSystemSettings(updatedSettings);
      
      // Save to Firebase immediately
      console.log('💾 Saving settings to Firebase...');
      const success = await updateSystemSettings(updatedSettings);
      
      if (success) {
        toast.success('✅ Dark mode logo uploaded and saved successfully!');
        console.log('✅ Dark logo settings saved to Firebase');
      } else {
        throw new Error('Failed to save dark logo settings to Firebase');
      }
    } catch (error) {
      console.error('❌ Error uploading dark logo:', error);
      
      if (error.code === 'storage/unauthorized') {
        toast.error('❌ Storage permission denied. Please check Firebase Storage rules.');
      } else if (error.code === 'storage/unknown') {
        toast.error('❌ Storage error. Please check your internet connection.');
      } else {
        toast.error(`❌ Failed to upload dark logo: ${error.message}`);
      }
    } finally {
      setLoading(false);
      // Clear the file input
      event.target.value = '';
    }
  };

  const handleDocumentUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (!files.length) return;

    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain',
      'image/jpeg',
      'image/png',
      'image/gif',
      'image/webp'
    ];

    const invalidFiles = files.filter(file => !allowedTypes.includes(file.type));
    if (invalidFiles.length > 0) {
      toast.error('Please select only PDF, Word, text, or image files');
      return;
    }

    try {
      setContentLoading(true);
      const uploadedFiles = [];

      for (const file of files) {
        const fileId = `content_${Date.now()}`;
        const storageRef = ref(storage, `documents/${fileId}/${file.name}`);
        const snapshot = await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(snapshot.ref);
        
        uploadedFiles.push({
          name: file.name,
          url: downloadURL,
          type: file.type,
          size: file.size,
          uploadedAt: new Date().toISOString()
        });
      }

      // Update newContent with uploaded files (no auto-save)
      setNewContent(prev => ({
        ...prev,
        attachments: [...(prev.attachments || []), ...uploadedFiles]
      }));

      toast.success(`✅ ${uploadedFiles.length} file(s) uploaded successfully to Firebase Storage!`);
      return uploadedFiles;
    } catch (error) {
      console.error('Error uploading documents:', error);
      toast.error(`❌ Failed to upload documents: ${error.message}`);
      return [];
    } finally {
      setContentLoading(false);
    }
  };

  const removeAttachment = (index) => {
    setNewContent(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
    toast.success('Attachment removed');
  };

  // Filter tabs based on user role - only Super Admin can see Reports and Event Logs
  const allTabs = [
    { id: 'reports', label: 'Reports', icon: BarChart3, superAdminOnly: true },
    { id: 'content', label: 'Content Management', icon: FileText },
    { id: 'leads', label: 'Leads', icon: Users },
    { id: 'jobs', label: 'Job Applications', icon: MessageSquare },
    { id: 'chats', label: 'Chat Logs', icon: MessageSquare },
    { id: 'users', label: 'Registered Users', icon: Users },
    { id: 'settings', label: 'System Settings', icon: Settings },
    { id: 'cms', label: 'CMS Settings', icon: Palette },
    { id: 'admins', label: 'Admin Users', icon: Key },
    { id: 'logs', label: 'Event Logs', icon: Clock, superAdminOnly: true }
  ];

  const tabs = allTabs.filter(tab => {
    if (tab.superAdminOnly) {
      return currentAdmin?.role === 'Super Admin' || currentAdmin?.role === 'super-admin';
    }
    return true;
  });

  // Load system settings from Firebase
  const loadSystemSettings = async () => {
    try {
      setSettingsLoading(true);
      const settings = await getSystemSettings();
      if (settings) {
        setSystemSettings(settings);
        console.log('✅ System settings loaded from Firebase:', settings);
        toast.success('✅ System settings loaded from Firebase');
      }
    } catch (error) {
      console.error('Error loading system settings:', error);
      toast.error('❌ Failed to load system settings');
    } finally {
      setSettingsLoading(false);
    }
  };

  // Initialize system settings when component mounts
  useEffect(() => {
    if (isLoggedIn && firebaseConnected) {
      loadSystemSettings();
    }
  }, [isLoggedIn, firebaseConnected]);

  const saveCMSSettings = async () => {
    try {
      setCmsLoading(true);
      console.log('🔄 Saving CMS settings to Firebase:', systemSettings);
      
      // Ensure we have the required settings structure
      if (!systemSettings.branding || !systemSettings.aiAssistant || !systemSettings.theme) {
        toast.error('❌ Invalid settings structure. Please refresh and try again.');
        return;
      }
      
      const success = await updateSystemSettings(systemSettings);
      
      if (success) {
        toast.success('✅ CMS settings saved successfully to Firebase!');
        console.log('✅ CMS settings saved successfully to Firebase');
        
        // Reload settings to confirm save
        await loadSystemSettings();
      } else {
        throw new Error('Failed to save CMS settings to Firebase');
      }
      
    } catch (error) {
      console.error('❌ Error saving CMS settings:', error);
      toast.error(`❌ Failed to save CMS settings: ${error.message}`);
    } finally {
      setCmsLoading(false);
    }
  };

  // Add new admin user
  const handleAddAdmin = async () => {
    if (!newAdmin.name || !newAdmin.email || !newAdmin.password) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      setAdminLoading(true);
      const addedAdmin = await addAdminUser(newAdmin);
      
      setAdminUsers(prev => [...prev, addedAdmin]);
      setNewAdmin({ name: '', email: '', password: '', role: 'admin' });
      setShowAddAdmin(false);
      
      // Log the admin creation event
      await logEvent(
        'admin',
        'admin_create',
        `Created new admin user: ${addedAdmin.name} (${addedAdmin.email})`,
        { 
          newAdminId: addedAdmin.id, 
          role: addedAdmin.role,
          adminRole: currentAdmin?.role
        },
        currentAdmin?.id,
        currentAdmin?.email
      );
      
      toast.success('✅ Admin user added successfully!');
    } catch (error) {
      console.error('Error adding admin:', error);
      
      // Handle specific Firebase permission errors
      if (error.message.includes('Permission denied') || error.message.includes('permission-denied')) {
        toast.error(
          '🔒 Firebase Permission Error: Please configure Firestore security rules to allow writes to the "system" collection. ' +
          'For development, you can use: allow read, write: if true;',
          { duration: 8000 }
        );
      } else if (error.message.includes('Firebase is currently unavailable')) {
        toast.error('🔥 Firebase is currently unavailable. Please check your internet connection and try again.');
      } else if (error.message.includes('Admin with this email already exists')) {
        toast.error('👤 An admin with this email already exists. Please use a different email.');
      } else {
        toast.error(`❌ Failed to add admin: ${error.message}`);
      }
    } finally {
      setAdminLoading(false);
    }
  };

  // Delete admin user
  const handleDeleteAdmin = async (adminId) => {
    if (adminUsers.length <= 1) {
      toast.error('Cannot delete the last admin user');
      return;
    }

    if (currentAdmin?.id === adminId) {
      toast.error('Cannot delete your own account');
      return;
    }

    if (window.confirm('Are you sure you want to delete this admin user?')) {
      try {
        setAdminLoading(true);
        const adminToDelete = adminUsers.find(admin => admin.id === adminId);
        await deleteAdminUser(adminId);
        
        setAdminUsers(prev => prev.filter(admin => admin.id !== adminId));
        
        // Log the admin deletion event
        await logEvent(
          'admin',
          'admin_delete',
          `Deleted admin user: ${adminToDelete?.name} (${adminToDelete?.email})`,
          { 
            deletedAdminId: adminId,
            adminRole: currentAdmin?.role
          },
          currentAdmin?.id,
          currentAdmin?.email
        );
        
        toast.success('✅ Admin user deleted successfully!');
      } catch (error) {
        console.error('Error deleting admin:', error);
        toast.error(`❌ Failed to delete admin: ${error.message}`);
      } finally {
        setAdminLoading(false);
      }
    }
  };

  // Toggle admin status
  const handleToggleAdminStatus = async (adminId, currentStatus) => {
    if (currentAdmin?.id === adminId) {
      toast.error('Cannot deactivate your own account');
      return;
    }

    try {
      setAdminLoading(true);
      const updatedAdmin = await updateAdminUser(adminId, { active: !currentStatus });
      
      setAdminUsers(prev => prev.map(admin => 
        admin.id === adminId ? updatedAdmin : admin
      ));
      
      toast.success(`✅ Admin ${!currentStatus ? 'activated' : 'deactivated'} successfully!`);
    } catch (error) {
      console.error('Error updating admin status:', error);
      toast.error(`❌ Failed to update admin status: ${error.message}`);
    } finally {
      setAdminLoading(false);
    }
  };

  // Logout function
  const handleLogout = async () => {
    // Log the logout event before clearing state
    if (currentAdmin) {
      await logEvent(
        'authentication',
        'admin_logout',
        `Admin user ${currentAdmin.name} logged out`,
        {
          adminRole: currentAdmin.role,
          logoutTime: new Date().toISOString(),
          sessionEnd: true
        },
        currentAdmin.id,
        currentAdmin.email
      );
    }
    
    setIsLoggedIn(false);
    setCurrentAdmin(null);
    setActiveTab('content');
    toast.success('Logged out successfully');
  };

  // Password reset functions
  const handlePasswordResetRequest = async (e) => {
    e.preventDefault();
    if (!resetEmail) {
      toast.error('Please enter your email address');
      return;
    }

    setResetLoading(true);
    try {
      const result = await createPasswordResetRequest(resetEmail);
      if (result.success) {
        const emailResult = await sendPasswordResetEmail(resetEmail, result.resetToken);
        
        // For development, show the reset link directly
        if (emailResult.resetLink) {
          setResetLink(emailResult.resetLink);
          toast.success('Password reset link generated! Check the console or use the link below.');
        } else {
          toast.success('Password reset instructions have been sent to your email');
        }
        
        setShowPasswordReset(false);
        setResetEmail('');
      }
    } catch (error) {
      console.error('Password reset request error:', error);
      toast.error(error.message || 'Failed to send password reset email');
    } finally {
      setResetLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    
    if (!newPassword || !confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return;
    }

    setResetLoading(true);
    try {
      const result = await resetAdminPassword(resetToken, newPassword);
      if (result.success) {
        toast.success('Password reset successfully! You can now login with your new password.');
        setShowResetForm(false);
        setResetToken('');
        setNewPassword('');
        setConfirmPassword('');
        setResetLink('');
      }
    } catch (error) {
      console.error('Password reset error:', error);
      toast.error(error.message || 'Failed to reset password');
    } finally {
      setResetLoading(false);
    }
  };

  // Check for reset token in URL on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    if (token) {
      setResetToken(token);
      setShowResetForm(true);
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  // Cleanup expired tokens periodically
  useEffect(() => {
    const cleanupInterval = setInterval(async () => {
      try {
        await cleanupExpiredResetTokens();
      } catch (error) {
        console.error('Token cleanup error:', error);
      }
    }, 60 * 60 * 1000); // Run every hour

    return () => clearInterval(cleanupInterval);
  }, []);

  // AI-powered content functions
  const generateAIKeywords = async () => {
    if (!newContent.subject || !newContent.details) {
      toast.error('Please enter subject and details first');
      return;
    }

    setKeywordLoading(true);
    try {
      // Use AI service to generate keywords
      const aiService = await import('../../services/aiService');
      const provider = aiService.default.getEnabledProvider(systemSettings);
      
      if (provider) {
        // Create a prompt for keyword generation
        const prompt = `Analyze the following content and generate 8-12 relevant keywords for better searchability. Return only the keywords separated by commas.

Subject: ${newContent.subject}
Category: ${newContent.category}
Details: ${newContent.details}

Generate keywords that would help users find this content when searching. Include both specific technical terms and general business terms.`;

        const keywords = await aiService.default.generateStreamingResponse(
          prompt,
          [],
          'en',
          systemSettings,
          () => {} // No streaming needed for keywords
        );

        // Clean up the response and set keywords
        const cleanKeywords = keywords
          .replace(/Keywords?:?\s*/i, '')
          .replace(/\n/g, ', ')
          .replace(/['"]/g, '')
          .trim();

        setNewContent(prev => ({
          ...prev,
          keywords: cleanKeywords
        }));

        toast.success('AI keywords generated successfully!');
      } else {
        // Fallback keyword generation
        const fallbackKeywords = generateFallbackKeywords(newContent.subject, newContent.details, newContent.category);
        setNewContent(prev => ({
          ...prev,
          keywords: fallbackKeywords
        }));
        toast.success('Keywords generated using fallback method');
      }
    } catch (error) {
      console.error('Error generating keywords:', error);
      // Fallback keyword generation
      const fallbackKeywords = generateFallbackKeywords(newContent.subject, newContent.details, newContent.category);
      setNewContent(prev => ({
        ...prev,
        keywords: fallbackKeywords
      }));
      toast.success('Keywords generated using fallback method');
    } finally {
      setKeywordLoading(false);
    }
  };

  const generateFallbackKeywords = (subject, details, category) => {
    const keywords = new Set();
    
    // Add category-based keywords
    const categoryKeywords = {
      'ai': ['artificial intelligence', 'machine learning', 'AI technology', 'automation', 'neural networks'],
      'services': ['business services', 'consulting', 'solutions', 'enterprise', 'professional services'],
      'products': ['software products', 'technology solutions', 'applications', 'platforms', 'tools'],
      'security': ['cybersecurity', 'data protection', 'security solutions', 'privacy', 'encryption'],
      'general': ['technology', 'innovation', 'digital transformation', 'business solutions']
    };
    
    if (categoryKeywords[category]) {
      categoryKeywords[category].forEach(kw => keywords.add(kw));
    }
    
    // Extract keywords from subject and details
    const text = `${subject} ${details}`.toLowerCase();
    const commonWords = ['the', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for', 'of', 'with', 'by', 'is', 'are', 'was', 'were', 'be', 'been', 'have', 'has', 'had', 'do', 'does', 'did', 'will', 'would', 'could', 'should', 'may', 'might', 'can', 'this', 'that', 'these', 'those', 'a', 'an'];
    
    const words = text.match(/\b\w{3,}\b/g) || [];
    words.forEach(word => {
      if (!commonWords.includes(word) && word.length > 3) {
        keywords.add(word);
      }
    });
    
    // Add some AI/tech related keywords
    const techKeywords = ['scalovate', 'AI', 'technology', 'digital', 'innovation', 'automation', 'intelligence'];
    techKeywords.forEach(kw => {
      if (text.includes(kw.toLowerCase())) {
        keywords.add(kw);
      }
    });
    
    return Array.from(keywords).slice(0, 10).join(', ');
  };

  const calculateContentQuality = () => {
    let score = 0;
    
    // Subject quality (20 points)
    if (newContent.subject) {
      if (newContent.subject.length >= 10) score += 20;
      else if (newContent.subject.length >= 5) score += 15;
      else score += 10;
    }
    
    // Details quality (40 points)
    if (newContent.details) {
      if (newContent.details.length >= 200) score += 40;
      else if (newContent.details.length >= 100) score += 30;
      else if (newContent.details.length >= 50) score += 20;
      else score += 10;
    }
    
    // Keywords quality (20 points)
    if (newContent.keywords) {
      const keywordCount = newContent.keywords.split(',').filter(k => k.trim()).length;
      if (keywordCount >= 8) score += 20;
      else if (keywordCount >= 5) score += 15;
      else if (keywordCount >= 3) score += 10;
      else score += 5;
    }
    
    // Category selected (10 points)
    if (newContent.category && newContent.category !== '') score += 10;
    
    // Attachments (10 points)
    if (newContent.attachments && newContent.attachments.length > 0) score += 10;
    
    return Math.min(score, 100);
  };

  const getQualityScoreColor = (score, isBackground = false) => {
    if (score >= 80) return isBackground ? 'bg-green-500' : 'text-green-600';
    if (score >= 60) return isBackground ? 'bg-yellow-500' : 'text-yellow-600';
    return isBackground ? 'bg-red-500' : 'text-red-600';
  };

  const getQualityRecommendations = () => {
    const recommendations = [];
    
    if (!newContent.subject || newContent.subject.length < 10) {
      recommendations.push('Add a more descriptive subject (10+ characters)');
    }
    
    if (!newContent.details || newContent.details.length < 100) {
      recommendations.push('Expand details section (100+ characters recommended)');
    }
    
    if (!newContent.keywords || newContent.keywords.split(',').filter(k => k.trim()).length < 5) {
      recommendations.push('Add more keywords (5+ recommended)');
    }
    
    if (!newContent.category) {
      recommendations.push('Select a category');
    }
    
    if (!newContent.attachments || newContent.attachments.length === 0) {
      recommendations.push('Consider adding relevant attachments');
    }
    
    if (recommendations.length === 0) {
      return 'Excellent! Your content meets all quality criteria.';
    }
    
    return recommendations.join(' • ');
  };

  // Document analysis functions
  const handleAIDocumentUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const allowedTypes = [
      'text/plain',
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];

    if (!allowedTypes.includes(file.type)) {
      toast.error('Please select a TXT, PDF, DOC, or DOCX file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) { // 10MB limit
      toast.error('File size must be less than 10MB');
      return;
    }

    setDocumentAnalysisLoading(true);
    try {
      // Extract text content from file
      let textContent = '';
      
      if (file.type === 'text/plain') {
        textContent = await file.text();
      } else {
        // For PDF and DOC files, we'll upload to Firebase and let AI analyze the URL
        // In a real implementation, you'd use a PDF/DOC parser
        const storageRef = ref(storage, `temp-analysis/${Date.now()}_${file.name}`);
        const snapshot = await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(snapshot.ref);
        
        // For now, we'll create a placeholder text for PDF/DOC files
        textContent = `Document uploaded: ${file.name}. This is a ${file.type} file that would need to be processed by a document parser in a production environment.`;
      }

      // Use AI to analyze the content
      const aiService = await import('../../services/aiService');
      const provider = aiService.default.getEnabledProvider(systemSettings);
      
      if (provider) {
        const analysisPrompt = `Analyze the following document content and extract:
1. A concise subject/title (max 100 characters)
2. The most appropriate category from: general, services, products, ai, security
3. A comprehensive analysis structured as at least 3 distinct paragraphs (minimum 400-800 words total):
   - First paragraph: Overview and main concepts
   - Second paragraph: Key details, features, or methodology
   - Third paragraph: Benefits, applications, or conclusions
   - Additional paragraphs as needed for comprehensive coverage
4. 8-12 relevant keywords (comma-separated)
5. Write entire content as an easy to read article in first person
5. A summary of the document in 300 words

Document content:
${textContent.substring(0, 3000)} ${textContent.length > 3000 ? '...' : ''}

Return the response in this exact JSON format:
{
  "subject": "extracted subject",
  "category": "selected category",
  "details": "comprehensive analysis with at least 3 paragraphs separated by double line breaks",
  "keywords": "keyword1, keyword2, keyword3, ..."
}`;

        const response = await aiService.default.generateStreamingResponse(
          analysisPrompt,
          [],
          'en',
          systemSettings,
          () => {} // No streaming needed for analysis
        );

        // Parse AI response
        try {
          // Try to extract JSON from response
          const jsonMatch = response.match(/\{[\s\S]*\}/);
          if (jsonMatch) {
            const parsed = JSON.parse(jsonMatch[0]);
            setAnalyzedContent(parsed);
            toast.success('Document analyzed successfully!');
          } else {
            throw new Error('Invalid JSON response');
          }
        } catch (parseError) {
          // Fallback parsing if JSON fails - ensure multiple paragraphs
          const extractedDetails = extractBetween(response, 'details', 'keywords') || '';
          let formattedDetails = extractedDetails;
          
          // If extracted details are too short or don't have multiple paragraphs, enhance them
          if (extractedDetails.length < 300 || !extractedDetails.includes('\n\n')) {
            const fileName = file.name.replace(/\.[^/.]+$/, "");
            formattedDetails = `Document Analysis Overview:\n${extractedDetails || `This document "${fileName}" has been processed and analyzed for content extraction.`}\n\nDetailed Content Review:\nThe document contains structured information that provides valuable insights and data points. The content has been evaluated for relevance and applicability to business operations and strategic initiatives.\n\nKey Findings and Applications:\nBased on the analysis, this document serves as an important resource that can be referenced for decision-making processes. The information contained within supports various operational requirements and can guide future planning activities.`;
          }
          
          const fallbackAnalysis = {
            subject: extractBetween(response, 'subject', 'category') || 'Document Analysis',
            category: extractBetween(response, 'category', 'details') || 'general',
            details: formattedDetails,
            keywords: extractBetween(response, 'keywords', '') || 'document, analysis, content'
          };
          setAnalyzedContent(fallbackAnalysis);
          toast.success('Document analyzed successfully!');
        }
      } else {
        // Fallback analysis without AI - generate multiple paragraphs
        const fileName = file.name.replace(/\.[^/.]+$/, "");
        const contentPreview = textContent.substring(0, 300);
        const fallbackAnalysis = {
          subject: fileName.substring(0, 50),
          category: 'general',
          details: `Document Overview:\nThis document titled "${fileName}" contains important information that has been uploaded for analysis. The content appears to cover various topics and concepts that may be relevant to business operations and strategic planning.\n\nContent Analysis:\nThe document includes detailed information and data that can be utilized for reference purposes. Based on the initial content review, this material contains structured information that could be valuable for decision-making processes and operational guidance.\n\nPractical Applications:\nThis document can serve as a reference resource for team members and stakeholders. The information contained within can be leveraged to support various business initiatives and provide insights for future planning and implementation strategies.`,
          keywords: 'document, content, analysis, reference, business, information, ' + fileName.toLowerCase().replace(/[^a-z0-9]/g, ' ').trim().split(' ').slice(0, 3).join(', ')
        };
        setAnalyzedContent(fallbackAnalysis);
        toast.success('Document processed with basic analysis');
      }
    } catch (error) {
      console.error('Error analyzing document:', error);
      toast.error('Failed to analyze document');
    } finally {
      setDocumentAnalysisLoading(false);
      // Clear the file input
      event.target.value = '';
    }
  };

  const extractBetween = (text, start, end) => {
    const startIndex = text.toLowerCase().indexOf(start.toLowerCase());
    if (startIndex === -1) return '';
    
    const afterStart = text.substring(startIndex + start.length);
    const endIndex = end ? afterStart.toLowerCase().indexOf(end.toLowerCase()) : afterStart.length;
    
    return afterStart.substring(0, endIndex > -1 ? endIndex : afterStart.length)
      .replace(/[":,\n]/g, '')
      .trim();
  };

  const applyAnalyzedContent = () => {
    if (analyzedContent) {
      setNewContent(prev => ({
        ...prev,
        subject: analyzedContent.subject,
        category: analyzedContent.category,
        details: analyzedContent.details,
        keywords: analyzedContent.keywords
      }));
      setAnalyzedContent(null);
      toast.success('Analysis applied to form!');
    }
  };

  // Reset to default settings function
  const resetToDefaults = () => {
    const defaultSettings = {
      theme: {
        primaryColor: '#ce6161',
        secondaryColor: '#000000',
        darkMode: false
      },
      branding: {
        logoUrl: '/scalovate_logo.png',
        darkLogoUrl: '',
        headerText: 'Paradigm Shift in Technology',
        footerText: 'Scalovate - AI Solutions & Enterprise Intelligence',
        copyright: '© 2025 Scalovate. All rights reserved.',
        locations: 'Qatar • Australia • Canada',
        headerBackgroundColor: 'linear-gradient(to right, rgba(255, 255, 255, 0), rgba(200, 200, 200, 0.5))',
        headerTextColor: '#1f2937',
        headerTransparency: 90,
        chatHeaderBackgroundColor: 'linear-gradient(to right, rgba(255, 255, 255, 0), rgba(200, 200, 200, 0.5))',
        chatHeaderTextColor: '#1f2937',
        chatHeaderTransparency: 85,
        footerBackgroundColor: 'linear-gradient(to right, rgba(255, 255, 255, 0), rgba(200, 200, 200, 0.5))',
        footerTextColor: '#1f2937',
        footerTransparency: 95,
        navigationLinks: [
          { name: 'Home', url: '/', active: true },
          { name: 'Services', url: '/services', active: true },
          { name: 'About', url: '/about', active: true },
          { name: 'Contact', url: '/contact', active: true }
        ],
        socialMediaLinks: [
          { name: 'LinkedIn', url: 'https://linkedin.com/company/scalovate', icon: 'linkedin', active: true },
          { name: 'YouTube', url: 'https://youtube.com/@scalovate', icon: 'youtube', active: true },
          { name: 'Facebook', url: 'https://facebook.com/scalovate', icon: 'facebook', active: true },
          { name: 'Instagram', url: 'https://instagram.com/scalovate', icon: 'instagram', active: true },
          { name: 'Twitter', url: 'https://twitter.com/scalovate', icon: 'twitter', active: false }
        ]
      },
      aiAssistant: {
        name: 'Jeje',
        avatarUrl: '/jeje_bot.png',
        description: 'AI Assistant from Scalovate',
        personality: 'Professional and helpful AI assistant specializing in technology solutions',
        welcomeMessage: 'Welcome to Scalovate! How can I assist you today?',
        communicationGuidelines: 'Be polite, helpful, and knowledgeable. Answer questions clearly and concisely. Use technical jargon when appropriate.',
        responseMaturity: 'generic'
      },
      aiModels: {
        ollama: { enabled: true, apiUrl: 'http://localhost:11434' },
        openai: { enabled: false, apiKey: '' },
        groq: { enabled: false, apiKey: '' },
        claude: { enabled: false, apiKey: '' },
        gemini: { enabled: false, apiKey: '' }
      },
      actionButtons: [
        { text: 'AI Solutions', key: 'ai-solutions', active: true },
        { text: 'Products', key: 'products', active: true },
        { text: 'About', key: 'about', active: true },
        { text: 'PrivateAI', key: 'private-ai', active: true },
        { text: 'AI Usecases', key: 'ai-usecases', active: true },
        { text: 'Contact', key: 'contact', active: true }
      ]
    };
    
    setSystemSettings(defaultSettings);
    toast.success('Settings reset to defaults!');
  };

  // Reset to default colors function
  const resetColorsToDefaults = () => {
    const updatedSettings = {
      ...systemSettings,
      branding: {
        ...systemSettings.branding,
        headerBackgroundColor: 'linear-gradient(to right, rgba(255, 255, 255, 0), rgba(200, 200, 200, 0.5))',
        headerTextColor: '#1f2937',
        headerTransparency: 90,
        chatHeaderBackgroundColor: 'linear-gradient(to right, rgba(255, 255, 255, 0), rgba(200, 200, 200, 0.5))',
        chatHeaderTextColor: '#1f2937',
        chatHeaderTransparency: 85,
        footerBackgroundColor: 'linear-gradient(to right, rgba(255, 255, 255, 0), rgba(200, 200, 200, 0.5))',
        footerTextColor: '#1f2937',
        footerTransparency: 95
      }
    };
    
    setSystemSettings(updatedSettings);
    toast.success('Colors reset to defaults!');
  };

  // Manual sync function for debugging
  const manualSyncUsers = async () => {
    try {
      setLoading(true);
      console.log('🔄 Starting manual user sync...');
      
      // Force reload users with multiple approaches
      await loadUsers();
      
      // Also try to sync current user if signed in
      const { syncAuthUsersToFirestore } = await import('../../services/authService');
      const syncResult = await syncAuthUsersToFirestore();
      
      console.log('✅ Manual sync completed:', syncResult);
      toast.success('User sync completed! Check console for details.');
    } catch (error) {
      console.error('❌ Manual sync failed:', error);
      toast.error('Manual sync failed. Check console for details.');
    } finally {
      setLoading(false);
    }
  };

  // Force reload all data
  const forceReloadAllData = async () => {
    try {
      setLoading(true);
      console.log('🔄 Force reloading all data...');
      
      await Promise.all([
        loadContent(),
        loadChats(),
        loadLeads(),
        loadJobApplications(),
        loadUsers(),
        loadAdminUsers()
      ]);
      
      console.log('✅ Force reload completed');
      toast.success('All data reloaded successfully!');
    } catch (error) {
      console.error('❌ Force reload failed:', error);
      toast.error('Force reload failed. Check console for details.');
    } finally {
      setLoading(false);
    }
  };

  // Debug functions for troubleshooting admin login issues
  const handleDebugAdminUsers = async () => {
    try {
      setLoading(true);
      console.log('🔧 Running admin users debug...');
      
      const result = await debugAdminUsers();
      
      if (result.success) {
        toast.success('Debug completed! Check console for details.');
      } else {
        toast.error(`Debug failed: ${result.error}`);
      }
    } catch (error) {
      console.error('❌ Debug failed:', error);
      toast.error('Debug failed. Check console for details.');
    } finally {
      setLoading(false);
    }
  };

  const handleForceInitializeAdmin = async () => {
    try {
      setLoading(true);
      console.log('🔧 Force initializing admin user...');
      
      const result = await forceInitializeAdminUser();
      
      if (result.success) {
        toast.success('Admin user initialized successfully!');
        console.log('✅ Default admin created:', result.admin);
      } else {
        toast.error('Failed to initialize admin user');
      }
    } catch (error) {
      console.error('❌ Force initialize failed:', error);
      toast.error(`Failed to initialize admin: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Edit admin user functions
  const handleEditAdmin = (admin) => {
    setEditingAdmin({
      id: admin.id,
      name: admin.name,
      email: admin.email,
      role: admin.role,
      active: admin.active,
      password: '' // Don't pre-fill password for security
    });
    setShowEditAdmin(true);
  };

  const handleUpdateAdmin = async () => {
    if (!editingAdmin.name || !editingAdmin.email) {
      toast.error('Please fill in required fields (name and email)');
      return;
    }

    try {
      setAdminLoading(true);
      
      // Prepare update data
      const updateData = {
        name: editingAdmin.name,
        email: editingAdmin.email,
        role: editingAdmin.role,
        active: editingAdmin.active
      };

      // Only include password if it's provided
      if (editingAdmin.password && editingAdmin.password.trim() !== '') {
        updateData.password = editingAdmin.password;
      }

      const updatedAdmin = await updateAdminUser(editingAdmin.id, updateData);
      
      // Update local state
      setAdminUsers(prev => prev.map(admin => 
        admin.id === editingAdmin.id ? updatedAdmin : admin
      ));
      
      // Update current admin if editing self
      if (currentAdmin?.id === editingAdmin.id) {
        setCurrentAdmin(updatedAdmin);
      }
      
      setShowEditAdmin(false);
      setEditingAdmin(null);
      toast.success('✅ Admin user updated successfully!');
    } catch (error) {
      console.error('Error updating admin:', error);
      
      if (error.message.includes('Permission denied') || error.message.includes('permission-denied')) {
        toast.error('🔒 Firebase Permission Error: Please check Firestore security rules.');
      } else if (error.message.includes('Admin with this email already exists')) {
        toast.error('👤 An admin with this email already exists. Please use a different email.');
      } else {
        toast.error(`❌ Failed to update admin: ${error.message}`);
      }
    } finally {
      setAdminLoading(false);
    }
  };

  const handleCancelEditAdmin = () => {
    setShowEditAdmin(false);
    setEditingAdmin(null);
  };

  // Bulk admin operations
  const handleBulkDeleteAdmins = async (adminIds) => {
    if (adminIds.includes(currentAdmin?.id)) {
      toast.error('Cannot delete your own account');
      return;
    }

    if (adminUsers.length - adminIds.length < 1) {
      toast.error('Cannot delete all admin users. At least one admin must remain.');
      return;
    }

    if (window.confirm(`Are you sure you want to delete ${adminIds.length} admin user(s)?`)) {
      try {
        setAdminLoading(true);
        
        for (const adminId of adminIds) {
          await deleteAdminUser(adminId);
        }
        
        setAdminUsers(prev => prev.filter(admin => !adminIds.includes(admin.id)));
        
        // Log the event
        await logEvent(
          'admin',
          'admin_bulk_delete',
          `Bulk deleted ${adminIds.length} admin users`,
          { 
            deletedAdminIds: adminIds,
            adminRole: currentAdmin?.role
          },
          currentAdmin?.id,
          currentAdmin?.email
        );
        
        toast.success(`✅ ${adminIds.length} admin user(s) deleted successfully!`);
      } catch (error) {
        console.error('Error bulk deleting admins:', error);
        toast.error(`❌ Failed to delete some admin users: ${error.message}`);
      } finally {
        setAdminLoading(false);
      }
    }
  };

  // Event Logs Functions
  const loadEventLogs = async (daysBack = 90) => {
    try {
      setLogsLoading(true);
      console.log(`🔍 Loading event logs for last ${daysBack} days...`);
      const logs = await getEventLogs(daysBack);
      console.log(`📊 Loaded ${logs.length} event logs:`, logs);
      setEventLogs(logs);
    } catch (error) {
      console.error('❌ Error loading event logs:', error);
      toast.error('Failed to load event logs');
    } finally {
      setLogsLoading(false);
    }
  };

  const loadEventLogsSummary = async (daysBack = 30) => {
    try {
      setReportsLoading(true);
      console.log(`📈 Loading event logs summary for last ${daysBack} days...`);
      const summary = await getEventLogsSummary(daysBack);
      console.log(`📊 Loaded summary:`, summary);
      setEventLogsSummary(summary);
    } catch (error) {
      console.error('❌ Error loading event logs summary:', error);
      toast.error('Failed to load reports data');
    } finally {
      setReportsLoading(false);
    }
  };

  const handleDeleteOldLogs = async () => {
    if (window.confirm('Are you sure you want to delete all logs older than 90 days? This action cannot be undone.')) {
      try {
        setLogsLoading(true);
        await deleteOldEventLogs();
        await loadEventLogs(logsDaysFilter);
        
        // Log the cleanup event
        await logEvent(
          'system',
          'logs_cleanup',
          'Deleted event logs older than 90 days',
          {
            adminRole: currentAdmin?.role
          },
          currentAdmin?.id,
          currentAdmin?.email
        );
        
        toast.success('✅ Old event logs deleted successfully');
      } catch (error) {
        console.error('Error deleting old logs:', error);
        toast.error('Failed to delete old logs');
      } finally {
        setLogsLoading(false);
      }
    }
  };

  const downloadEventLogs = (daysBack = 30) => {
    const filteredLogs = eventLogs.filter(log => {
      const logDate = new Date(log.timestamp);
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - daysBack);
      return logDate >= cutoffDate;
    });

    const csvData = filteredLogs.map(log => ({
      Timestamp: new Date(log.timestamp).toLocaleString(),
      'Event Type': log.type || log.eventType,
      'User Email': log.userEmail || 'System',
      'User ID': log.userId || 'N/A',
      Details: log.details,
      'IP Address': log.ipAddress || 'N/A',
      'User Agent': log.userAgent || 'N/A'
    }));

    exportToCSV(csvData, `event_logs_${daysBack}days_${new Date().toISOString().split('T')[0]}.csv`);
  };

  const downloadReportsData = (daysBack = 30) => {
    const summary = eventLogsSummary;
    
    // Create CSV data from summary
    const csvData = [];
    
    // Add event types summary
    Object.entries(summary.eventsByType || {}).forEach(([type, count]) => {
      csvData.push({
        Category: 'Event Type',
        Name: type,
        Count: count,
        Percentage: ((count / summary.totalEvents) * 100).toFixed(2) + '%'
      });
    });
    
    // Add users summary
    Object.entries(summary.eventsByUser || {}).forEach(([user, count]) => {
      csvData.push({
        Category: 'User Activity',
        Name: user,
        Count: count,
        Percentage: ((count / summary.totalEvents) * 100).toFixed(2) + '%'
      });
    });
    
    // Add daily summary
    Object.entries(summary.eventsByDate || {}).forEach(([date, count]) => {
      csvData.push({
        Category: 'Daily Activity',
        Name: date,
        Count: count,
        Percentage: ((count / summary.totalEvents) * 100).toFixed(2) + '%'
      });
    });

    exportToCSV(csvData, `reports_summary_${daysBack}days_${new Date().toISOString().split('T')[0]}.csv`);
  };

  // Handle data migration
  const handleDataMigration = async () => {
    if (!window.confirm('This will migrate existing Firebase data to create historical event logs. This process may take a few minutes. Continue?')) {
      return;
    }

    setMigrationLoading(true);
    
    try {
      console.log('🚀 Starting data migration...');
      toast.loading('Migrating existing data to event logs...', { id: 'migration' });
      
      const result = await runDataMigration();
      
      if (result && result.success) {
        toast.success(
          `Migration completed successfully!\n\n` +
          `Total Events: ${result.totalEvents}\n` +
          `Admin Events: ${result.eventBreakdown.adminEvents}\n` +
          `User Events: ${result.eventBreakdown.userEvents}\n` +
          `Chat Events: ${result.eventBreakdown.chatEvents}\n` +
          `Lead Events: ${result.eventBreakdown.leadEvents}\n` +
          `Job Events: ${result.eventBreakdown.jobEvents}\n` +
          `Content Events: ${result.eventBreakdown.contentEvents}`,
          { id: 'migration', duration: 8000 }
        );
        
        // Refresh event logs and reports data
        await loadEventLogs(logsDaysFilter);
        await loadEventLogsSummary(reportsDaysFilter);
        
        // Log the migration action
        await logEvent('system', 'data_migration_completed', `Data migration completed with ${result.totalEvents} events migrated`);
        
      } else {
        toast.error(
          `Migration completed with ${result?.errors || 'unknown'} errors.\n\n` +
          `Total Events: ${result?.totalEvents || 0}\n` +
          `Check console for details.`,
          { id: 'migration', duration: 6000 }
        );
      }
      
    } catch (error) {
      console.error('Migration error:', error);
      toast.error('Migration failed. Check console for details.', { id: 'migration' });
    } finally {
      setMigrationLoading(false);
    }
  };

  // Handle generating test event logs
  const handleGenerateTestLogs = async () => {
    setTestLogsLoading(true);
    
    try {
      console.log('🧪 Generating test event logs...');
      toast.loading('Generating test event logs...', { id: 'testlogs' });
      
      const testEvents = [
        {
          type: 'authentication',
          action: 'admin_login',
          details: 'Super Admin logged in successfully',
          metadata: { userRole: 'Super Admin', loginMethod: 'email_password' }
        },
        {
          type: 'admin_activity',
          action: 'create_user',
          details: 'New admin user created',
          metadata: { userRole: 'Admin', targetUser: 'test@example.com' }
        },
        {
          type: 'ui_changes',
          action: 'color_update',
          details: 'Header color changed',
          metadata: { changes: { headerColor: '#ff0000', previousColor: '#000000' } }
        },
        {
          type: 'content_management',
          action: 'create_content',
          details: 'New content item added',
          metadata: { contentId: 'test-content-001', contentType: 'article' }
        },
        {
          type: 'chat_activity',
          action: 'new_session',
          details: 'Chat session started',
          metadata: { chatId: 'chat-001', messageCount: 0 }
        },
        {
          type: 'system_activity',
          action: 'database_update',
          details: 'Database schema updated',
          metadata: { affectedCollection: 'users', documentId: 'schema-v1' }
        }
      ];

      const eventIds = [];
      for (const event of testEvents) {
        const eventId = await logEvent(
          event.type,
          event.action,
          event.details,
          event.metadata,
          currentAdmin?.id || 'test-user-id',
          currentAdmin?.email || 'test@scalovate.com'
        );
        if (eventId) {
          eventIds.push(eventId);
        }
        // Small delay between events
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      toast.success(`Generated ${eventIds.length} test event logs successfully!`, { id: 'testlogs' });
      
      // Refresh event logs and reports data
      await loadEventLogs(logsDaysFilter);
      await loadEventLogsSummary(reportsDaysFilter);
      
    } catch (error) {
      console.error('Test logs generation error:', error);
      toast.error('Failed to generate test logs. Check console for details.', { id: 'testlogs' });
    } finally {
      setTestLogsLoading(false);
    }
  };

  // Load event logs when tab changes to reports or logs
  useEffect(() => {
    if (isLoggedIn && firebaseConnected && (activeTab === 'reports' || activeTab === 'logs')) {
      if (activeTab === 'reports') {
        loadEventLogsSummary(reportsDaysFilter);
      } else if (activeTab === 'logs') {
        loadEventLogs(logsDaysFilter);
      }
    }
  }, [activeTab, isLoggedIn, firebaseConnected, reportsDaysFilter, logsDaysFilter]);

  // Filter admin users
  const filteredAdminUsers = adminUsers.filter(admin => {
    const matchesSearch = admin.name?.toLowerCase().includes(adminSearchTerm.toLowerCase()) ||
                         admin.email?.toLowerCase().includes(adminSearchTerm.toLowerCase());
    const matchesRole = adminRoleFilter === 'all' || admin.role === adminRoleFilter;
    return matchesSearch && matchesRole;
  });

  // Get role statistics
  const adminRoleStats = {
    total: adminUsers.length,
    'super-admin': adminUsers.filter(admin => admin.role === 'super-admin').length,
    'admin': adminUsers.filter(admin => admin.role === 'admin').length,
    'moderator': adminUsers.filter(admin => admin.role === 'moderator').length,
    active: adminUsers.filter(admin => admin.active).length,
    inactive: adminUsers.filter(admin => !admin.active).length
  };

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <AnimatePresence mode="wait">
          {showResetForm ? (
            // Password Reset Form
            <motion.div
              key="reset-form"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md"
            >
              <div className="text-center mb-8">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Reset Password</h1>
                <p className="text-gray-600">Enter your new password</p>
              </div>

              <form onSubmit={handlePasswordReset} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    New Password
                  </label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter new password"
                    required
                    minLength={6}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Confirm Password
                  </label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Confirm new password"
                    required
                    minLength={6}
                  />
                </div>

                <motion.button
                  type="submit"
                  disabled={resetLoading}
                  className="w-full bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  whileHover={{ scale: resetLoading ? 1 : 1.02 }}
                  whileTap={{ scale: resetLoading ? 1 : 0.98 }}
                >
                  {resetLoading ? 'Resetting Password...' : 'Reset Password'}
                </motion.button>

                <button
                  type="button"
                  onClick={() => {
                    setShowResetForm(false);
                    setResetToken('');
                    setNewPassword('');
                    setConfirmPassword('');
                  }}
                  className="w-full text-gray-600 hover:text-gray-800 transition-colors flex items-center justify-center space-x-2"
                >
                  <ArrowLeft size={16} />
                  <span>Back to Login</span>
                </button>
              </form>
            </motion.div>
          ) : showPasswordReset ? (
            // Password Reset Request Form
            <motion.div
              key="reset-request"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md"
            >
              <div className="text-center mb-8">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Forgot Password</h1>
                <p className="text-gray-600">Enter your email to receive reset instructions</p>
              </div>

              <form onSubmit={handlePasswordResetRequest} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="admin@scalovate.com"
                    required
                  />
                </div>

                <motion.button
                  type="submit"
                  disabled={resetLoading}
                  className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                  whileHover={{ scale: resetLoading ? 1 : 1.02 }}
                  whileTap={{ scale: resetLoading ? 1 : 0.98 }}
                >
                  <Mail size={16} />
                  <span>{resetLoading ? 'Sending...' : 'Send Reset Link'}</span>
                </motion.button>

                <button
                  type="button"
                  onClick={() => {
                    setShowPasswordReset(false);
                    setResetEmail('');
                  }}
                  className="w-full text-gray-600 hover:text-gray-800 transition-colors flex items-center justify-center space-x-2"
                >
                  <ArrowLeft size={16} />
                  <span>Back to Login</span>
                </button>
              </form>

              {resetLink && (
                <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800 mb-2">
                    <strong>Development Mode:</strong> Use this link to reset your password:
                  </p>
                  <a
                    href={resetLink}
                    className="text-blue-600 hover:text-blue-800 text-sm break-all underline"
                    onClick={() => {
                      const url = new URL(resetLink);
                      const token = url.searchParams.get('token');
                      if (token) {
                        setResetToken(token);
                        setShowResetForm(true);
                        setShowPasswordReset(false);
                        setResetLink('');
                      }
                    }}
                  >
                    {resetLink}
                  </a>
                </div>
              )}
            </motion.div>
          ) : (
            // Login Form
            <motion.div
              key="login-form"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md"
            >
              <div className="text-center mb-8">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Admin Portal</h1>
                <p className="text-gray-600">Scalovate AI Management System</p>
                <div className="flex items-center justify-center mt-4">
                  {firebaseConnected ? (
                    <div className="flex items-center text-green-600">
                      <Wifi size={16} className="mr-2" />
                      <span className="text-sm">Connected</span>
                    </div>
                  ) : (
                    <div className="flex items-center text-red-600">
                      <WifiOff size={16} className="mr-2" />
                      <span className="text-sm">Disconnected</span>
                    </div>
                  )}
                </div>
              </div>

              <form onSubmit={handleLogin} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={loginData.email}
                    onChange={(e) => setLoginData({...loginData, email: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="admin@scalovate.com"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Password
                  </label>
                  <input
                    type="password"
                    value={loginData.password}
                    onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="••••••••"
                    required
                  />
                </div>

                <motion.button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  whileHover={{ scale: loading ? 1 : 1.02 }}
                  whileTap={{ scale: loading ? 1 : 0.98 }}
                >
                  {loading ? 'Logging in...' : 'Login to Dashboard'}
                </motion.button>

                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => setShowPasswordReset(true)}
                    className="text-blue-600 hover:text-blue-800 text-sm transition-colors"
                  >
                    Forgot your password?
                  </button>
                </div>
              </form>

              {/* Debug Section - Only show if not connected or in development */}
              {(!firebaseConnected || process.env.NODE_ENV === 'development') && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <p className="text-xs text-gray-500 text-center mb-3">Debug Tools</p>
                  <div className="space-y-2">
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          setLoading(true);
                          const result = await debugAdminUsers();
                          if (result.success) {
                            toast.success('Debug completed! Check console for details.');
                          } else {
                            toast.error(`Debug failed: ${result.error}`);
                          }
                        } catch (error) {
                          console.error('❌ Debug failed:', error);
                          toast.error('Debug failed. Check console for details.');
                        } finally {
                          setLoading(false);
                        }
                      }}
                      disabled={loading}
                      className="w-full text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 py-2 px-3 rounded transition-colors disabled:opacity-50"
                    >
                      Debug Admin Users
                    </button>
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          setLoading(true);
                          const result = await forceInitializeAdminUser();
                          if (result.success) {
                            toast.success('Admin user initialized successfully!');
                            console.log('✅ Default admin created:', result.admin);
                          } else {
                            toast.error('Failed to initialize admin user');
                          }
                        } catch (error) {
                          console.error('❌ Force initialize failed:', error);
                          toast.error(`Failed to initialize admin: ${error.message}`);
                        } finally {
                          setLoading(false);
                        }
                      }}
                      disabled={loading}
                      className="w-full text-xs bg-yellow-100 hover:bg-yellow-200 text-yellow-700 py-2 px-3 rounded transition-colors disabled:opacity-50"
                    >
                      Force Initialize Admin
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  return (
    <div className={`h-screen flex flex-col overflow-hidden transition-colors duration-300 ${
      darkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'
    }`}>
      {/* Header */}
      <header className={`flex-shrink-0 shadow-sm border-b transition-colors duration-300 ${
        darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <img src="/scalovate_logo.png" alt="Scalovate" className="h-8 w-auto" />
              <h1 className="text-xl font-semibold">Admin Dashboard</h1>
              {currentAdmin && (
                <div className="hidden md:flex items-center space-x-2 text-sm">
                  <span className="text-gray-500">Welcome,</span>
                  <span className="font-medium">{currentAdmin.name}</span>
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    currentAdmin.role === 'super-admin' 
                      ? 'bg-purple-100 text-purple-800'
                      : 'bg-blue-100 text-blue-800'
                  }`}>
                    {currentAdmin.role}
                  </span>
                </div>
              )}
            </div>
            
            <div className="flex items-center space-x-4">
              <button
                onClick={exportAllData}
                disabled={loading}
                className={`px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 ${
                  darkMode 
                    ? 'bg-green-600 hover:bg-green-700 text-white' 
                    : 'bg-green-600 hover:bg-green-700 text-white'
                } disabled:opacity-50`}
              >
                <Download className="w-4 h-4" />
                <span>Export All Data</span>
              </button>
              
              <button
                onClick={() => {
                  setDarkMode(!darkMode);
                  setSystemSettings(prev => ({
                    ...prev,
                    theme: { ...prev.theme, darkMode: !darkMode }
                  }));
                }}
                className={`p-2 rounded-lg transition-colors ${
                  darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200'
                }`}
              >
                {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>

              <button
                onClick={handleLogout}
                className={`px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 ${
                  darkMode 
                    ? 'bg-red-600 hover:bg-red-700 text-white' 
                    : 'bg-red-600 hover:bg-red-700 text-white'
                }`}
              >
                <X className="w-4 h-4" />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Firebase Connection Warning */}
        {!firebaseConnected && (
          <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <WifiOff className="h-5 w-5 text-yellow-400" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">
                  Firebase Connection Issue
                </h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>
                    Unable to connect to Firebase. This may cause issues with adding admin users.
                    Please check the <code className="bg-yellow-100 px-1 rounded">FIREBASE_SETUP.md</code> file for troubleshooting steps.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Dashboard Overview */}
        <div className="mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
            {/* Firebase Status */}
            <div className={`p-6 rounded-lg shadow-sm ${
              darkMode ? 'bg-gray-800' : 'bg-white'
            }`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Firebase Status</p>
                  <p className={`text-lg font-semibold ${
                    firebaseConnected ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {firebaseConnected ? 'Connected' : 'Disconnected'}
                  </p>
                </div>
                {firebaseConnected ? (
                  <Wifi className="w-8 h-8 text-green-600" />
                ) : (
                  <WifiOff className="w-8 h-8 text-red-600" />
                )}
              </div>
            </div>

            {/* Content Stats */}
            <div className={`p-6 rounded-lg shadow-sm ${
              darkMode ? 'bg-gray-800' : 'bg-white'
            }`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Content Items</p>
                  <p className="text-lg font-semibold">{content.length}</p>
                  <p className="text-xs text-gray-500">
                    {content.filter(c => c.active).length} active
                  </p>
                </div>
                <FileText className="w-8 h-8 text-blue-600" />
              </div>
            </div>

            {/* Users Stats */}
            <div className={`p-6 rounded-lg shadow-sm ${
              darkMode ? 'bg-gray-800' : 'bg-white'
            }`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Registered Users</p>
                  <p className="text-lg font-semibold">{users.length}</p>
                  <p className="text-xs text-gray-500">
                    {users.filter(u => u.emailVerified).length} verified
                  </p>
                </div>
                <Users className="w-8 h-8 text-purple-600" />
              </div>
            </div>

            {/* Leads Stats */}
            <div className={`p-6 rounded-lg shadow-sm ${
              darkMode ? 'bg-gray-800' : 'bg-white'
            }`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Total Leads</p>
                  <p className="text-lg font-semibold">{leads.length}</p>
                  <p className="text-xs text-gray-500">
                    {leads.filter(l => l.status === 'new').length} new
                  </p>
                </div>
                <Users className="w-8 h-8 text-green-600" />
              </div>
            </div>

            {/* Chat Stats */}
            <div className={`p-6 rounded-lg shadow-sm ${
              darkMode ? 'bg-gray-800' : 'bg-white'
            }`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Chat Sessions</p>
                  <p className="text-lg font-semibold">{chats.length}</p>
                  <p className="text-xs text-gray-500">
                    {chats.filter(c => {
                      if (!c.timestamp?.toDate) return false;
                      const today = new Date();
                      const chatDate = c.timestamp.toDate();
                      return chatDate.toDateString() === today.toDateString();
                    }).length} today
                  </p>
                </div>
                <MessageSquare className="w-8 h-8 text-orange-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="mb-8">
          <nav className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 xl:grid-cols-5 gap-2 md:gap-3">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center justify-center space-x-1 md:space-x-2 px-2 md:px-3 py-2 text-xs md:text-sm font-medium rounded-lg transition-colors whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'bg-blue-600 text-white'
                      : darkMode
                      ? 'text-gray-300 hover:text-white hover:bg-gray-700'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <Icon className="w-3 h-3 md:w-4 md:h-4 flex-shrink-0" />
                  <span className="truncate">{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Content Management Tab */}
        {activeTab === 'content' && (
          <div className="space-y-6">
            {/* AI Document Analysis */}
            <div className={`p-6 rounded-lg shadow-sm ${
              darkMode ? 'bg-gray-800' : 'bg-white'
            }`}>
              <h2 className="text-lg font-semibold mb-4">AI Document Analysis</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Upload documents (TXT, PDF, DOC) and let AI automatically generate subject, category, details, and keywords
              </p>
              
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">
                  Upload Document for AI Analysis
                </label>
                <input
                  type="file"
                  accept=".txt,.pdf,.doc,.docx"
                  onChange={handleAIDocumentUpload}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                  }`}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Supported: TXT, PDF, DOC, DOCX files (max 10MB)
                </p>
              </div>
              
              {documentAnalysisLoading && (
                <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    <div>
                      <p className="text-sm font-medium">Analyzing document with AI...</p>
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        Extracting content and generating metadata
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {analyzedContent && (
                <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <h3 className="text-sm font-medium mb-3">AI Analysis Results:</h3>
                  <div className="space-y-2 text-sm">
                    <div><strong>Subject:</strong> {analyzedContent.subject}</div>
                    <div><strong>Category:</strong> {analyzedContent.category}</div>
                    <div><strong>Keywords:</strong> {analyzedContent.keywords}</div>
                    <div><strong>Details:</strong> {analyzedContent.details.substring(0, 200)}...</div>
                  </div>
                  <div className="mt-3 flex space-x-2">
                    <button
                      onClick={applyAnalyzedContent}
                      className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors text-sm"
                    >
                      Apply to Form
                    </button>
                    <button
                      onClick={() => setAnalyzedContent(null)}
                      className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition-colors text-sm"
                    >
                      Discard
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Add New Content Form */}
            <div className={`p-6 rounded-lg shadow-sm ${
              darkMode ? 'bg-gray-800' : 'bg-white'
            }`}>
              <h2 className="text-lg font-semibold mb-4">Add New Content</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <input
                  type="text"
                  placeholder="Subject"
                  value={newContent.subject}
                  onChange={(e) => setNewContent({...newContent, subject: e.target.value})}
                  className={`px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                  }`}
                />
                
                <select
                  value={newContent.category}
                  onChange={(e) => setNewContent({...newContent, category: e.target.value})}
                  className={`px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                  }`}
                >
                  <option value="general">General</option>
                  <option value="services">Services</option>
                  <option value="products">Products</option>
                  <option value="ai">AI Technology</option>
                  <option value="security">Security</option>
                </select>
              </div>
              
              <textarea
                placeholder="Details"
                value={newContent.details}
                onChange={(e) => setNewContent({...newContent, details: e.target.value})}
                rows={4}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-4 ${
                  darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                }`}
              />
              
              <input
                type="text"
                placeholder="Keywords (comma separated)"
                value={newContent.keywords}
                onChange={(e) => setNewContent({...newContent, keywords: e.target.value})}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-2 ${
                  darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                }`}
              />
              
              {/* AI-Powered Keyword Generation */}
              <div className="mb-4">
                <button
                  onClick={generateAIKeywords}
                  disabled={!newContent.subject || !newContent.details || keywordLoading}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 disabled:opacity-50 text-sm"
                >
                  <Sparkles className="w-4 h-4" />
                  <span>{keywordLoading ? 'Generating...' : 'Generate AI Keywords'}</span>
                </button>
                <p className="text-xs text-gray-500 mt-1">
                  AI will analyze your content and suggest relevant keywords for better searchability
                </p>
              </div>
              
              {/* Content Quality Score */}
              {newContent.subject && newContent.details && (
                <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">Content Quality Score</span>
                    <span className={`text-sm font-bold ${getQualityScoreColor(calculateContentQuality())}`}>
                      {calculateContentQuality()}/100
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                    <div 
                      className={`h-2 rounded-full transition-all duration-300 ${getQualityScoreColor(calculateContentQuality(), true)}`}
                      style={{ width: `${calculateContentQuality()}%` }}
                    ></div>
                  </div>
                  <div className="mt-2 text-xs text-gray-600 dark:text-gray-400">
                    {getQualityRecommendations()}
                  </div>
                </div>
              )}
              
              {/* File Upload */}
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2">
                  Attachments (PDF, Word, Images)
                </label>
                <input
                  type="file"
                  multiple
                  accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.gif,.webp"
                  onChange={handleDocumentUpload}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                  }`}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Supported: PDF, Word documents, text files, and images (max 10MB each)
                </p>
                
                {/* Show uploaded attachments */}
                {newContent.attachments && newContent.attachments.length > 0 && (
                  <div className="mt-3 space-y-2">
                    <p className="text-sm font-medium">Uploaded Files:</p>
                    {newContent.attachments.map((file, index) => (
                      <div key={index} className={`flex items-center justify-between p-2 border rounded ${
                        darkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-200 bg-gray-50'
                      }`}>
                        <div className="flex items-center space-x-2">
                          <FileText className="w-4 h-4" />
                          <span className="text-sm">{file.name}</span>
                          <span className="text-xs text-gray-500">
                            ({(file.size / 1024 / 1024).toFixed(2)} MB)
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <a
                            href={file.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 text-sm"
                          >
                            View
                          </a>
                          <button
                            onClick={() => removeAttachment(index)}
                            className="text-red-600 hover:text-red-800"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="flex items-center justify-between">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={newContent.active}
                    onChange={(e) => setNewContent({...newContent, active: e.target.checked})}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm">Active</span>
                </label>
                
                <button
                  onClick={saveContent}
                  disabled={contentLoading}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors flex items-center space-x-2 disabled:opacity-50"
                >
                  <Save className="w-4 h-4" />
                  <span>{contentLoading ? 'Saving...' : 'Save Content'}</span>
                </button>
              </div>
            </div>

            {/* Search and Filter */}
            <div className={`p-4 rounded-lg shadow-sm ${
              darkMode ? 'bg-gray-800' : 'bg-white'
            }`}>
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <input
                    type="text"
                    placeholder="Search content..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                    }`}
                  />
                </div>
                
                <select
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                  className={`px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                  }`}
                >
                  <option value="all">All Categories</option>
                  <option value="general">General</option>
                  <option value="services">Services</option>
                  <option value="products">Products</option>
                  <option value="ai">AI Technology</option>
                  <option value="security">Security</option>
                </select>
              </div>
            </div>

            {/* Content List */}
            <div className="space-y-4">
              {filteredContent.map((item) => (
                <div key={item.id} className={`p-6 rounded-lg shadow-sm ${
                  darkMode ? 'bg-gray-800' : 'bg-white'
                }`}>
                  {editingContent === item.id ? (
                    <div className="space-y-4">
                      <input
                        type="text"
                        value={item.subject}
                        onChange={(e) => {
                          const updatedContent = content.map(c => 
                            c.id === item.id ? {...c, subject: e.target.value} : c
                          );
                          setContent(updatedContent);
                        }}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                          darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                        }`}
                      />
                      
                      <textarea
                        value={item.details}
                        onChange={(e) => {
                          const updatedContent = content.map(c => 
                            c.id === item.id ? {...c, details: e.target.value} : c
                          );
                          setContent(updatedContent);
                        }}
                        rows={4}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                          darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                        }`}
                      />
                      
                      <div className="flex space-x-2">
                        <button
                          onClick={() => updateContent(item.id, item)}
                          className="bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700 transition-colors flex items-center space-x-1"
                        >
                          <Save className="w-4 h-4" />
                          <span>Save</span>
                        </button>
                        
                        <button
                          onClick={() => setEditingContent(null)}
                          className="bg-gray-600 text-white px-3 py-1 rounded hover:bg-gray-700 transition-colors flex items-center space-x-1"
                        >
                          <X className="w-4 h-4" />
                          <span>Cancel</span>
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="flex justify-between items-start mb-2">
                        <h3 className="text-lg font-semibold">{item.subject}</h3>
                        <div className="flex space-x-2">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            item.active 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {item.active ? 'Active' : 'Inactive'}
                          </span>
                          
                          <button
                            onClick={() => setEditingContent(item.id)}
                            className="text-blue-600 hover:text-blue-800 transition-colors"
                          >
                            <Edit3 className="w-4 h-4" />
                          </button>
                          
                          <button
                            onClick={() => deleteContent(item.id)}
                            className="text-red-600 hover:text-red-800 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      
                      <p className={`mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        {item.details}
                      </p>
                      
                      <div className="flex flex-wrap gap-2 mb-2">
                        <span className={`px-2 py-1 text-xs rounded ${
                          darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {item.category}
                        </span>
                        
                        {item.keywords && item.keywords.split(',').map((keyword, index) => (
                          <span key={index} className={`px-2 py-1 text-xs rounded ${
                            darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'
                          }`}>
                            {keyword.trim()}
                          </span>
                        ))}
                      </div>
                      
                      {item.attachments && item.attachments.length > 0 && (
                        <div className="mt-2">
                          <p className="text-sm font-medium mb-1">Attachments:</p>
                          <div className="flex flex-wrap gap-2">
                            {item.attachments.map((attachment, index) => (
                              <a
                                key={index}
                                href={attachment.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary hover:underline text-sm flex items-center space-x-1"
                              >
                                <FileText className="w-3 h-3" />
                                <span>{attachment.name}</span>
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* System Settings Tab */}
        {activeTab === 'settings' && (
          <div className="space-y-6">
            <div className={`p-6 rounded-lg shadow-sm ${
              darkMode ? 'bg-gray-800' : 'bg-white'
            }`}>
              <h2 className="text-lg font-semibold mb-4">AI Model Configuration</h2>
              
              <div className="space-y-4">
                {Object.entries(systemSettings.aiModels).map(([model, config]) => (
                  <div key={model} className={`p-4 border rounded-lg ${
                    darkMode ? 'border-gray-600' : 'border-gray-200'
                  }`}>
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-medium capitalize">{model}</h3>
                      <label className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={config.enabled}
                          onChange={(e) => setSystemSettings(prev => ({
                            ...prev,
                            aiModels: {
                              ...prev.aiModels,
                              [model]: { ...config, enabled: e.target.checked }
                            }
                          }))}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm">Enabled</span>
                      </label>
                    </div>
                    
                    {model === 'ollama' ? (
                      <input
                        type="text"
                        placeholder="API URL"
                        value={config.apiUrl}
                        onChange={(e) => setSystemSettings(prev => ({
                          ...prev,
                          aiModels: {
                            ...prev.aiModels,
                            [model]: { ...config, apiUrl: e.target.value }
                          }
                        }))}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                          darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                        }`}
                      />
                    ) : (
                      <div className="relative">
                        <input
                          type={showApiKeys[model] ? "text" : "password"}
                          placeholder="API Key"
                          value={config.apiKey}
                          onChange={(e) => setSystemSettings(prev => ({
                            ...prev,
                            aiModels: {
                              ...prev.aiModels,
                              [model]: { ...config, apiKey: e.target.value }
                            }
                          }))}
                          className={`w-full px-3 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                            darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                          }`}
                        />
                        <button
                          type="button"
                          onClick={() => toggleApiKeyVisibility(model)}
                          className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        >
                          {showApiKeys[model] ? (
                            <EyeOff className="w-4 h-4 text-gray-400" />
                          ) : (
                            <Eye className="w-4 h-4 text-gray-400" />
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              
              <button
                onClick={saveSystemSettings}
                disabled={settingsLoading}
                className="mt-4 bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors flex items-center space-x-2 disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                <span>{settingsLoading ? 'Saving...' : 'Save Settings'}</span>
              </button>
            </div>

            {/* Action Buttons Settings */}
            <div className={`p-6 rounded-lg shadow-sm ${
              darkMode ? 'bg-gray-800' : 'bg-white'
            }`}>
              <h2 className="text-lg font-semibold mb-4">Chat Action Buttons</h2>
              
              <div className="space-y-4">
                {systemSettings.actionButtons?.map((button, index) => (
                  <div key={index} className="flex items-center space-x-4 p-4 border rounded-lg">
                    <div className="flex-1">
                      <input
                        type="text"
                        value={button.text}
                        onChange={(e) => {
                          const updatedButtons = [...systemSettings.actionButtons];
                          updatedButtons[index] = { ...button, text: e.target.value };
                          setSystemSettings(prev => ({
                            ...prev,
                            actionButtons: updatedButtons
                          }));
                        }}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                          darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                        }`}
                        placeholder="Button text"
                      />
                    </div>
                    <div className="flex-1">
                      <input
                        type="text"
                        value={button.key}
                        onChange={(e) => {
                          const updatedButtons = [...systemSettings.actionButtons];
                          updatedButtons[index] = { ...button, key: e.target.value };
                          setSystemSettings(prev => ({
                            ...prev,
                            actionButtons: updatedButtons
                          }));
                        }}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                          darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                        }`}
                        placeholder="Button key (e.g., ai-solutions)"
                      />
                    </div>
                    <div className="flex items-center space-x-2">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={button.active}
                          onChange={(e) => {
                            const updatedButtons = [...systemSettings.actionButtons];
                            updatedButtons[index] = { ...button, active: e.target.checked };
                            setSystemSettings(prev => ({
                              ...prev,
                              actionButtons: updatedButtons
                            }));
                          }}
                          className="mr-2"
                        />
                        <span className="text-sm">Active</span>
                      </label>
                      <button
                        onClick={() => {
                          const updatedButtons = systemSettings.actionButtons.filter((_, i) => i !== index);
                          setSystemSettings(prev => ({
                            ...prev,
                            actionButtons: updatedButtons
                          }));
                        }}
                        className="text-red-600 hover:text-red-800 p-1"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
                
                <button
                  onClick={() => {
                    const newButton = { text: 'New Button', key: 'new-button', active: true };
                    setSystemSettings(prev => ({
                      ...prev,
                      actionButtons: [...(prev.actionButtons || []), newButton]
                    }));
                  }}
                  className="w-full border-2 border-dashed border-gray-300 rounded-lg p-4 text-gray-500 hover:border-gray-400 hover:text-gray-600 transition-colors flex items-center justify-center space-x-2"
                >
                  <Plus className="w-4 h-4" />
                  <span>Add New Button</span>
                </button>
              </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end">
              <button
                onClick={saveCMSSettings}
                disabled={cmsLoading}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors flex items-center space-x-2 disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                <span>{cmsLoading ? 'Saving...' : 'Save All Settings'}</span>
              </button>
            </div>

            {/* Navigation Links Management */}
            <div className={`p-6 rounded-lg shadow-sm ${
              darkMode ? 'bg-gray-800' : 'bg-white'
            }`}>
              <h2 className="text-lg font-semibold mb-4">Navigation Links</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Manage navigation links that will appear in the header dropdown menu
              </p>
              
              <div className="space-y-4">
                {systemSettings.branding.navigationLinks?.map((link, index) => (
                  <div key={index} className="flex items-center space-x-4 p-4 border rounded-lg">
                    <div className="flex-1">
                      <input
                        type="text"
                        value={link.name}
                        onChange={(e) => {
                          const updatedLinks = [...systemSettings.branding.navigationLinks];
                          updatedLinks[index] = { ...link, name: e.target.value };
                          setSystemSettings(prev => ({
                            ...prev,
                            branding: { ...prev.branding, navigationLinks: updatedLinks }
                          }));
                        }}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                          darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                        }`}
                        placeholder="Link Name (e.g., Home, Contact, Services)"
                      />
                    </div>
                    <div className="flex-1">
                      <input
                        type="url"
                        value={link.url}
                        onChange={(e) => {
                          const updatedLinks = [...systemSettings.branding.navigationLinks];
                          updatedLinks[index] = { ...link, url: e.target.value };
                          setSystemSettings(prev => ({
                            ...prev,
                            branding: { ...prev.branding, navigationLinks: updatedLinks }
                          }));
                        }}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                          darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                        }`}
                        placeholder="URL (e.g., /contact, https://example.com)"
                      />
                    </div>
                    <div className="flex items-center space-x-2">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={link.active}
                          onChange={(e) => {
                            const updatedLinks = [...systemSettings.branding.navigationLinks];
                            updatedLinks[index] = { ...link, active: e.target.checked };
                            setSystemSettings(prev => ({
                              ...prev,
                              branding: { ...prev.branding, navigationLinks: updatedLinks }
                            }));
                          }}
                          className="mr-2"
                        />
                        <span className="text-sm">Active</span>
                      </label>
                      <button
                        onClick={() => {
                          const updatedLinks = systemSettings.branding.navigationLinks.filter((_, i) => i !== index);
                          setSystemSettings(prev => ({
                            ...prev,
                            branding: { ...prev.branding, navigationLinks: updatedLinks }
                          }));
                        }}
                        className="text-red-600 hover:text-red-800 p-1"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
                
                <button
                  onClick={() => {
                    const newLink = { name: 'New Link', url: '/', active: true };
                    setSystemSettings(prev => ({
                      ...prev,
                      branding: { ...prev.branding, navigationLinks: [...(prev.branding.navigationLinks || []), newLink] }
                    }));
                  }}
                  className="w-full border-2 border-dashed border-gray-300 rounded-lg p-4 text-gray-500 hover:border-gray-400 hover:text-gray-600 transition-colors flex items-center justify-center space-x-2"
                >
                  <Plus className="w-4 h-4" />
                  <span>Add New Navigation Link</span>
                </button>
              </div>
            </div>

            {/* Social Media Links Management */}
            <div className={`p-6 rounded-lg shadow-sm ${
              darkMode ? 'bg-gray-800' : 'bg-white'
            }`}>
              <h2 className="text-lg font-semibold mb-4">Social Media Links</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Manage social media links that will appear in the footer
              </p>
              
              <div className="space-y-4">
                {systemSettings.branding.socialMediaLinks?.map((link, index) => (
                  <div key={index} className="flex items-center space-x-4 p-4 border rounded-lg">
                    <div className="flex-1">
                      <input
                        type="text"
                        value={link.name}
                        onChange={(e) => {
                          const updatedLinks = [...systemSettings.branding.socialMediaLinks];
                          updatedLinks[index] = { ...link, name: e.target.value };
                          setSystemSettings(prev => ({
                            ...prev,
                            branding: { ...prev.branding, socialMediaLinks: updatedLinks }
                          }));
                        }}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                          darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                        }`}
                        placeholder="Platform Name (e.g., LinkedIn, Facebook)"
                      />
                    </div>
                    <div className="flex-1">
                      <input
                        type="url"
                        value={link.url}
                        onChange={(e) => {
                          const updatedLinks = [...systemSettings.branding.socialMediaLinks];
                          updatedLinks[index] = { ...link, url: e.target.value };
                          setSystemSettings(prev => ({
                            ...prev,
                            branding: { ...prev.branding, socialMediaLinks: updatedLinks }
                          }));
                        }}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                          darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                        }`}
                        placeholder="Social Media URL"
                      />
                    </div>
                    <div className="flex-1">
                      <select
                        value={link.icon}
                        onChange={(e) => {
                          const updatedLinks = [...systemSettings.branding.socialMediaLinks];
                          updatedLinks[index] = { ...link, icon: e.target.value };
                          setSystemSettings(prev => ({
                            ...prev,
                            branding: { ...prev.branding, socialMediaLinks: updatedLinks }
                          }));
                        }}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                          darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                        }`}
                      >
                        <option value="linkedin">LinkedIn</option>
                        <option value="youtube">YouTube</option>
                        <option value="facebook">Facebook</option>
                        <option value="instagram">Instagram</option>
                        <option value="twitter">Twitter</option>
                        <option value="github">GitHub</option>
                        <option value="tiktok">TikTok</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                    <div className="flex items-center space-x-2">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={link.active}
                          onChange={(e) => {
                            const updatedLinks = [...systemSettings.branding.socialMediaLinks];
                            updatedLinks[index] = { ...link, active: e.target.checked };
                            setSystemSettings(prev => ({
                              ...prev,
                              branding: { ...prev.branding, socialMediaLinks: updatedLinks }
                            }));
                          }}
                          className="mr-2"
                        />
                        <span className="text-sm">Active</span>
                      </label>
                      <button
                        onClick={() => {
                          const updatedLinks = systemSettings.branding.socialMediaLinks.filter((_, i) => i !== index);
                          setSystemSettings(prev => ({
                            ...prev,
                            branding: { ...prev.branding, socialMediaLinks: updatedLinks }
                          }));
                        }}
                        className="text-red-600 hover:text-red-800 p-1"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
                
                <button
                  onClick={() => {
                    const newLink = { name: 'New Platform', url: 'https://', icon: 'other', active: true };
                    setSystemSettings(prev => ({
                      ...prev,
                      branding: { ...prev.branding, socialMediaLinks: [...(prev.branding.socialMediaLinks || []), newLink] }
                    }));
                  }}
                  className="w-full border-2 border-dashed border-gray-300 rounded-lg p-4 text-gray-500 hover:border-gray-400 hover:text-gray-600 transition-colors flex items-center justify-center space-x-2"
                >
                  <Plus className="w-4 h-4" />
                  <span>Add New Social Media Link</span>
                </button>
              </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end">
              <button
                onClick={saveCMSSettings}
                disabled={cmsLoading}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors flex items-center space-x-2 disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                <span>{cmsLoading ? 'Saving...' : 'Save All Settings'}</span>
              </button>
            </div>
          </div>
        )}

        {/* Leads Tab */}
        {activeTab === 'leads' && (
          <div className={`p-6 rounded-lg shadow-sm ${
            darkMode ? 'bg-gray-800' : 'bg-white'
          }`}>
            <h2 className="text-lg font-semibold mb-4">Leads ({leads.length})</h2>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className={darkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Company</th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Interest</th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Date</th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${darkMode ? 'divide-gray-600' : 'divide-gray-200'}`}>
                  {leads.map((lead) => (
                    <tr key={lead.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {lead.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {lead.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {lead.company || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {lead.interest || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {lead.createdAt?.toDate?.()?.toLocaleDateString() || 'N/A'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Chat Logs Tab */}
        {activeTab === 'chats' && (
          <div className={`p-6 rounded-lg shadow-sm ${
            darkMode ? 'bg-gray-800' : 'bg-white'
          }`}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Chat Logs ({chats.length})</h2>
              <button
                onClick={() => exportToCSV(chats, 'chat_logs')}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center space-x-2"
              >
                <Download className="w-4 h-4" />
                <span>Export CSV</span>
              </button>
            </div>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className={darkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">User</th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">User Message</th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">AI Response</th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Language</th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${darkMode ? 'divide-gray-600' : 'divide-gray-200'}`}>
                  {filteredChats.slice(0, 50).map((chat) => (
                    <tr key={chat.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {chat.timestamp?.toDate?.()?.toLocaleString() || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {chat.isAnonymous ? (
                          <span className="text-gray-500 italic">Anonymous</span>
                        ) : (
                          <div>
                            <div className="font-medium">{chat.userName || 'Unknown'}</div>
                            <div className="text-xs text-gray-500">{chat.userEmail || 'No email'}</div>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm max-w-xs truncate">
                        {chat.userMessage || 'N/A'}
                      </td>
                      <td className="px-6 py-4 text-sm max-w-xs truncate">
                        {chat.aiResponse || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {chat.language || 'en'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {chats.length > 50 && (
              <div className="mt-4 text-center text-sm text-gray-500">
                Showing latest 50 chat logs. Export CSV for complete data.
              </div>
            )}
            
            {chats.length === 0 && (
              <div className="text-center py-8">
                <MessageSquare className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-500">No chat logs found</p>
                <div className="text-sm text-gray-400 space-y-2 mt-4">
                  <p>Chat logs will appear here when users interact with the AI assistant.</p>
                  <button
                    onClick={loadChats}
                    className="mt-2 bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-xs"
                  >
                    Refresh Chat Logs
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Registered Users Tab */}
        {activeTab === 'users' && (
          <div className={`p-6 rounded-lg shadow-sm ${
            darkMode ? 'bg-gray-800' : 'bg-white'
          }`}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Registered Users ({users.length})</h2>
              <div className="flex space-x-2">
                <button
                  onClick={loadUsers}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center space-x-2"
                >
                  <RefreshCw className="w-4 h-4" />
                  <span>Refresh</span>
                </button>
                <button
                  onClick={() => {
                    toast('Users will appear here when they sign in. If users are missing, ask them to sign out and sign in again.', {
                      icon: 'ℹ️',
                      duration: 6000
                    });
                  }}
                  className="bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center space-x-2"
                >
                  <Users className="w-4 h-4" />
                  <span>Sync Info</span>
                </button>
                <button
                  onClick={() => exportToCSV(users, 'registered_users')}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center space-x-2"
                >
                  <Download className="w-4 h-4" />
                  <span>Export CSV</span>
                </button>
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className={darkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Provider</th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Email Verified</th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Chat Count</th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Joined</th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Last Login</th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${darkMode ? 'divide-gray-600' : 'divide-gray-200'}`}>
                  {users.map((user) => (
                    <tr key={user.id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-sm font-bold mr-3">
                            {user.displayName ? user.displayName.charAt(0).toUpperCase() : user.email.charAt(0).toUpperCase()}
                          </div>
                          <div className="text-sm font-medium">
                            {user.displayName || 'No name'}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {user.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          user.provider === 'google.com' 
                            ? 'bg-red-100 text-red-800'
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {user.provider === 'google.com' ? 'Google' : 'Email'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          user.emailVerified 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {user.emailVerified ? 'Verified' : 'Pending'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {user.chatCount || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {user.createdAt?.toDate?.()?.toLocaleDateString() || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {user.lastLogin?.toDate?.()?.toLocaleDateString() || 'Never'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            {users.length === 0 && (
              <div className="text-center py-8">
                <Users className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-500">No registered users found in Firestore</p>
                <div className="text-sm text-gray-400 space-y-2 mt-4">
                  <p>Users will appear here when they:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Sign up with email and verify their account</li>
                    <li>Sign in with Google (if enabled)</li>
                    <li>Sign in again if they registered before this system was updated</li>
                  </ul>
                  <p className="mt-4 font-medium">If users are missing, ask them to sign out and sign in again.</p>
                  
                  {/* Debug Information */}
                  <div className="mt-6 p-4 bg-gray-100 dark:bg-gray-700 rounded-lg">
                    <h4 className="font-medium mb-2">Debug Information:</h4>
                    <div className="text-xs space-y-1">
                      <p>• Firebase Connected: {firebaseConnected ? '✅ Yes' : '❌ No'}</p>
                      <p>• Users Collection Query: Checking /users collection</p>
                      <p>• Last Refresh: {new Date().toLocaleTimeString()}</p>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      <button
                        onClick={async () => {
                          console.log('🔍 Manual debug check...');
                          console.log('Firebase connected:', firebaseConnected);
                          await loadUsers();
                          toast('Debug refresh completed. Check console for details.', {
                            icon: '🔍',
                            duration: 3000
                          });
                        }}
                        className="bg-gray-600 hover:bg-gray-700 text-white px-3 py-1 rounded text-xs"
                      >
                        Debug Refresh
                      </button>
                      <button
                        onClick={manualSyncUsers}
                        disabled={loading}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-xs disabled:opacity-50"
                      >
                        Sync Users
                      </button>
                      <button
                        onClick={forceReloadAllData}
                        disabled={loading}
                        className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-xs disabled:opacity-50"
                      >
                        Reload All
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Job Applications Tab */}
        {activeTab === 'jobs' && (
          <div className={`p-6 rounded-lg shadow-sm ${
            darkMode ? 'bg-gray-800' : 'bg-white'
          }`}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Job Applications ({jobApplications.length})</h2>
              <button
                onClick={() => exportToCSV(jobApplications, 'job_applications')}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center space-x-2"
              >
                <Download className="w-4 h-4" />
                <span>Export CSV</span>
              </button>
            </div>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className={darkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Phone</th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Role</th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Location</th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Date</th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${darkMode ? 'divide-gray-600' : 'divide-gray-200'}`}>
                  {filteredJobApplications.map((job) => (
                    <tr key={job.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        {job.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {job.email}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {job.phone || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {job.role || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {job.city && job.country ? `${job.city}, ${job.country}` : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {job.createdAt?.toDate?.()?.toLocaleDateString() || 'N/A'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* CMS Settings Tab */}
        {activeTab === 'cms' && (
          <div className="space-y-6">
            {/* Branding Section */}
            <div className={`p-6 rounded-lg shadow-sm ${
              darkMode ? 'bg-gray-800' : 'bg-white'
            }`}>
              <h2 className="text-lg font-semibold mb-4">Branding & Logo</h2>
              
              <div className="space-y-6">
                {/* Logo Upload */}
                <div>
                  <label className="block text-sm font-medium mb-2">Company Logo (Light Mode)</label>
                  <div className="flex items-center space-x-4">
                    <div className="flex-shrink-0">
                      <img
                        src={systemSettings.branding.logoUrl}
                        alt="Current Logo"
                        className="w-16 h-16 object-contain border rounded-lg"
                        onError={(e) => {
                          e.target.src = '/scalovate_logo.png';
                        }}
                      />
                    </div>
                    <div className="flex-1">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        className="hidden"
                        id="logo-upload"
                      />
                      <label
                        htmlFor="logo-upload"
                        className="cursor-pointer bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 w-fit"
                      >
                        <Upload className="w-4 h-4" />
                        <span>Upload Light Mode Logo</span>
                      </label>
                      <p className="text-xs text-gray-500 mt-1">
                        Recommended: PNG or JPG, max 2MB
                      </p>
                    </div>
                  </div>
                </div>

                {/* Dark Mode Logo Upload */}
                <div>
                  <label className="block text-sm font-medium mb-2">Company Logo (Dark Mode)</label>
                  <div className="flex items-center space-x-4">
                    <div className="flex-shrink-0">
                      <img
                        src={systemSettings.branding.darkLogoUrl || systemSettings.branding.logoUrl}
                        alt="Dark Mode Logo"
                        className="w-16 h-16 object-contain border rounded-lg bg-gray-800"
                        onError={(e) => {
                          e.target.src = '/scalovate_logo.png';
                        }}
                      />
                    </div>
                    <div className="flex-1">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleDarkLogoUpload}
                        className="hidden"
                        id="dark-logo-upload"
                      />
                      <label
                        htmlFor="dark-logo-upload"
                        className="cursor-pointer bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 w-fit"
                      >
                        <Upload className="w-4 h-4" />
                        <span>Upload Dark Mode Logo</span>
                      </label>
                      <p className="text-xs text-gray-500 mt-1">
                        Optional: White/light colored logo for dark mode
                      </p>
                    </div>
                  </div>
                </div>

                {/* Header and Footer Text */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Header Text</label>
                    <input
                      type="text"
                      value={systemSettings.branding.headerText}
                      onChange={(e) => setSystemSettings(prev => ({
                        ...prev,
                        branding: { ...prev.branding, headerText: e.target.value }
                      }))}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                      }`}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">Footer Text</label>
                    <input
                      type="text"
                      value={systemSettings.branding.footerText}
                      onChange={(e) => setSystemSettings(prev => ({
                        ...prev,
                        branding: { ...prev.branding, footerText: e.target.value }
                      }))}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                      }`}
                    />
                  </div>
                </div>

                {/* Header Colors */}
                <div>
                  <h3 className="text-md font-medium mb-3">Header Styling</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Header Background Color</label>
                      <input
                        type="color"
                        value={systemSettings.branding.headerBackgroundColor || '#ffffff'}
                        onChange={(e) => setSystemSettings(prev => ({
                          ...prev,
                          branding: { ...prev.branding, headerBackgroundColor: e.target.value }
                        }))}
                        className="w-full h-10 border rounded-lg"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2">Header Text Color</label>
                      <input
                        type="color"
                        value={systemSettings.branding.headerTextColor || '#000000'}
                        onChange={(e) => setSystemSettings(prev => ({
                          ...prev,
                          branding: { ...prev.branding, headerTextColor: e.target.value }
                        }))}
                        className="w-full h-10 border rounded-lg"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2">Header Transparency (%)</label>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={systemSettings.branding.headerTransparency || 100}
                        onChange={(e) => setSystemSettings(prev => ({
                          ...prev,
                          branding: { ...prev.branding, headerTransparency: parseInt(e.target.value) }
                        }))}
                        className="w-full"
                      />
                      <span className="text-xs text-gray-500">
                        {systemSettings.branding.headerTransparency || 100}%
                      </span>
                    </div>
                  </div>
                </div>

                {/* Chat Header Colors */}
                <div>
                  <h3 className="text-md font-medium mb-3">Chat Header Styling</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Chat Header Background</label>
                      <input
                        type="color"
                        value={systemSettings.branding.chatHeaderBackgroundColor || '#f3f4f6'}
                        onChange={(e) => setSystemSettings(prev => ({
                          ...prev,
                          branding: { ...prev.branding, chatHeaderBackgroundColor: e.target.value }
                        }))}
                        className="w-full h-10 border rounded-lg"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2">Chat Header Text Color</label>
                      <input
                        type="color"
                        value={systemSettings.branding.chatHeaderTextColor || '#000000'}
                        onChange={(e) => setSystemSettings(prev => ({
                          ...prev,
                          branding: { ...prev.branding, chatHeaderTextColor: e.target.value }
                        }))}
                        className="w-full h-10 border rounded-lg"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2">Chat Header Transparency (%)</label>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={systemSettings.branding.chatHeaderTransparency || 60}
                        onChange={(e) => setSystemSettings(prev => ({
                          ...prev,
                          branding: { ...prev.branding, chatHeaderTransparency: parseInt(e.target.value) }
                        }))}
                        className="w-full"
                      />
                      <span className="text-xs text-gray-500">
                        {systemSettings.branding.chatHeaderTransparency || 60}%
                      </span>
                    </div>
                  </div>
                </div>

                {/* Footer Colors */}
                <div>
                  <h3 className="text-md font-medium mb-3">Footer Styling</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Footer Background Color</label>
                      <input
                        type="color"
                        value={systemSettings.branding.footerBackgroundColor || '#000000'}
                        onChange={(e) => setSystemSettings(prev => ({
                          ...prev,
                          branding: { ...prev.branding, footerBackgroundColor: e.target.value }
                        }))}
                        className="w-full h-10 border rounded-lg"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2">Footer Text Color</label>
                      <input
                        type="color"
                        value={systemSettings.branding.footerTextColor || '#ffffff'}
                        onChange={(e) => setSystemSettings(prev => ({
                          ...prev,
                          branding: { ...prev.branding, footerTextColor: e.target.value }
                        }))}
                        className="w-full h-10 border rounded-lg"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2">Footer Transparency (%)</label>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={systemSettings.branding.footerTransparency || 100}
                        onChange={(e) => setSystemSettings(prev => ({
                          ...prev,
                          branding: { ...prev.branding, footerTransparency: parseInt(e.target.value) }
                        }))}
                        className="w-full"
                      />
                      <span className="text-xs text-gray-500">
                        {systemSettings.branding.footerTransparency || 100}%
                      </span>
                    </div>
                  </div>
                </div>

                {/* Copyright and Locations */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Copyright Text</label>
                    <input
                      type="text"
                      value={systemSettings.branding.copyright}
                      onChange={(e) => setSystemSettings(prev => ({
                        ...prev,
                        branding: { ...prev.branding, copyright: e.target.value }
                      }))}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                      }`}
                      placeholder="© 2025 Your Company. All rights reserved."
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">Locations</label>
                    <input
                      type="text"
                      value={systemSettings.branding.locations}
                      onChange={(e) => setSystemSettings(prev => ({
                        ...prev,
                        branding: { ...prev.branding, locations: e.target.value }
                      }))}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                      }`}
                      placeholder="Country • Country • Country"
                    />
                  </div>
                </div>

                {/* Color Theme */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Primary Color</label>
                    <input
                      type="color"
                      value={systemSettings.theme.primaryColor}
                      onChange={(e) => setSystemSettings(prev => ({
                        ...prev,
                        theme: { ...prev.theme, primaryColor: e.target.value }
                      }))}
                      className="w-full h-10 border rounded-lg"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">Secondary Color</label>
                    <input
                      type="color"
                      value={systemSettings.theme.secondaryColor}
                      onChange={(e) => setSystemSettings(prev => ({
                        ...prev,
                        theme: { ...prev.theme, secondaryColor: e.target.value }
                      }))}
                      className="w-full h-10 border rounded-lg"
                    />
                  </div>
                </div>

                {/* Reset Colors Button */}
                <div className="flex justify-start">
                  <button
                    onClick={resetColorsToDefaults}
                    className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded-lg transition-colors flex items-center space-x-2"
                  >
                    <X className="w-4 h-4" />
                    <span>Reset Colors to Defaults</span>
                  </button>
                </div>
              </div>
            </div>

            {/* AI Assistant Settings */}
            <div className={`p-6 rounded-lg shadow-sm ${
              darkMode ? 'bg-gray-800' : 'bg-white'
            }`}>
              <h2 className="text-lg font-semibold mb-4">AI Assistant Settings</h2>
              
              <div className="space-y-6">
                {/* Avatar Upload */}
                <div>
                  <label className="block text-sm font-medium mb-2">AI Assistant Avatar</label>
                  <div className="flex items-center space-x-4">
                    <div className="flex-shrink-0">
                      <img
                        src={systemSettings.aiAssistant.avatarUrl}
                        alt="AI Avatar"
                        className="w-16 h-16 object-cover border rounded-full"
                        onError={(e) => {
                          e.target.src = '/jeje_bot.png';
                        }}
                      />
                    </div>
                    <div className="flex-1">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleAvatarUpload}
                        className="hidden"
                        id="avatar-upload"
                      />
                      <label
                        htmlFor="avatar-upload"
                        className="cursor-pointer bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 w-fit"
                      >
                        <Upload className="w-4 h-4" />
                        <span>Upload New Avatar</span>
                      </label>
                      <p className="text-xs text-gray-500 mt-1">
                        Recommended: Square image, PNG or JPG, max 2MB
                      </p>
                    </div>
                  </div>
                </div>

                {/* AI Assistant Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">AI Assistant Name</label>
                    <input
                      type="text"
                      value={systemSettings.aiAssistant.name}
                      onChange={(e) => setSystemSettings(prev => ({
                        ...prev,
                        aiAssistant: { ...prev.aiAssistant, name: e.target.value }
                      }))}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                      }`}
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-2">AI Description</label>
                    <input
                      type="text"
                      value={systemSettings.aiAssistant.description}
                      onChange={(e) => setSystemSettings(prev => ({
                        ...prev,
                        aiAssistant: { ...prev.aiAssistant, description: e.target.value }
                      }))}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                      }`}
                    />
                  </div>
                </div>

                {/* Welcome Message */}
                <div>
                  <label className="block text-sm font-medium mb-2">Welcome Message</label>
                  <textarea
                    value={systemSettings.aiAssistant.welcomeMessage || ''}
                    onChange={(e) => setSystemSettings(prev => ({
                      ...prev,
                      aiAssistant: { ...prev.aiAssistant, welcomeMessage: e.target.value }
                    }))}
                    rows={3}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                    }`}
                    placeholder="Enter the welcome message that users will see when they start a conversation..."
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    This message will be displayed when users first interact with the AI assistant.
                  </p>
                </div>

                {/* Personality Traits */}
                <div>
                  <label className="block text-sm font-medium mb-2">Personality Traits</label>
                  <textarea
                    value={systemSettings.aiAssistant.personality}
                    onChange={(e) => setSystemSettings(prev => ({
                      ...prev,
                      aiAssistant: { ...prev.aiAssistant, personality: e.target.value }
                    }))}
                    rows={4}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                    }`}
                    placeholder="Describe the AI's personality traits, communication style, and behavior patterns..."
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Define how the AI should communicate, its tone, expertise areas, and interaction style.
                  </p>
                </div>

                {/* Communication Guidelines */}
                <div>
                  <label className="block text-sm font-medium mb-2">Communication Guidelines</label>
                  <textarea
                    value={systemSettings.aiAssistant.communicationGuidelines || ''}
                    onChange={(e) => setSystemSettings(prev => ({
                      ...prev,
                      aiAssistant: { ...prev.aiAssistant, communicationGuidelines: e.target.value }
                    }))}
                    rows={3}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                    }`}
                    placeholder="Specific guidelines for how the AI should respond to different types of questions..."
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Additional instructions for handling specific scenarios or conversation types.
                  </p>
                </div>

                {/* Response Maturity Setting */}
                <div>
                  <label className="block text-sm font-medium mb-2">Response Maturity</label>
                  <select
                    value={systemSettings.aiAssistant.responseMaturity || 'generic'}
                    onChange={(e) => setSystemSettings(prev => ({
                      ...prev,
                      aiAssistant: { ...prev.aiAssistant, responseMaturity: e.target.value }
                    }))}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                    }`}
                  >
                    <option value="generic">Generic</option>
                    <option value="seniority-based">Seniority Based</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Generic: AI provides standard responses to all users. Seniority Based: AI adjusts response complexity based on user's designation and seniority level.
                  </p>
                </div>
              </div>
            </div>

            {/* User Permissions Settings */}
            <div className={`p-6 rounded-lg shadow-sm ${
              darkMode ? 'bg-gray-800' : 'bg-white'
            }`}>
              <h2 className="text-lg font-semibold mb-4">User Permissions</h2>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <label className="block text-sm font-medium">Allow Chat Deletion</label>
                    <p className="text-xs text-gray-500">Allow users to delete their chat history</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={systemSettings.userPermissions?.allowChatDeletion || false}
                      onChange={(e) => setSystemSettings(prev => ({
                        ...prev,
                        userPermissions: { 
                          ...prev.userPermissions, 
                          allowChatDeletion: e.target.checked 
                        }
                      }))}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <label className="block text-sm font-medium">Allow Profile Editing</label>
                    <p className="text-xs text-gray-500">Allow users to edit their profile information</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={systemSettings.userPermissions?.allowProfileEdit || false}
                      onChange={(e) => setSystemSettings(prev => ({
                        ...prev,
                        userPermissions: { 
                          ...prev.userPermissions, 
                          allowProfileEdit: e.target.checked 
                        }
                      }))}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <label className="block text-sm font-medium">Allow Password Changes</label>
                    <p className="text-xs text-gray-500">Allow users to change their passwords</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={systemSettings.userPermissions?.allowPasswordChange || false}
                      onChange={(e) => setSystemSettings(prev => ({
                        ...prev,
                        userPermissions: { 
                          ...prev.userPermissions, 
                          allowPasswordChange: e.target.checked 
                        }
                      }))}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                  </label>
                </div>
              </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end">
              <button
                onClick={saveCMSSettings}
                disabled={cmsLoading}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg transition-colors flex items-center space-x-2 disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                <span>{cmsLoading ? 'Saving...' : 'Save All Settings'}</span>
              </button>
            </div>
          </div>
        )}

        {/* Admin Users Tab */}
        {activeTab === 'admins' && (
          <div className="space-y-6">
            {/* Admin Statistics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className={`p-4 rounded-lg shadow-sm ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Total Admins</p>
                    <p className="text-2xl font-bold">{adminRoleStats.total}</p>
                  </div>
                  <Users className="w-8 h-8 text-blue-500" />
                </div>
              </div>
              
              <div className={`p-4 rounded-lg shadow-sm ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Super Admins</p>
                    <p className="text-2xl font-bold text-purple-600">{adminRoleStats['super-admin']}</p>
                  </div>
                  <Key className="w-8 h-8 text-purple-500" />
                </div>
              </div>
              
              <div className={`p-4 rounded-lg shadow-sm ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Active</p>
                    <p className="text-2xl font-bold text-green-600">{adminRoleStats.active}</p>
                  </div>
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                  </div>
                </div>
              </div>
              
              <div className={`p-4 rounded-lg shadow-sm ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Inactive</p>
                    <p className="text-2xl font-bold text-red-600">{adminRoleStats.inactive}</p>
                  </div>
                  <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                    <div className="w-3 h-3 bg-red-500 rounded-full"></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Search and Filter */}
            <div className={`p-6 rounded-lg shadow-sm ${
              darkMode ? 'bg-gray-800' : 'bg-white'
            }`}>
              <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0 md:space-x-4">
                <div className="flex-1 max-w-md">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      placeholder="Search admins by name or email..."
                      value={adminSearchTerm}
                      onChange={(e) => setAdminSearchTerm(e.target.value)}
                      className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                      }`}
                    />
                  </div>
                </div>
                
                <div className="flex items-center space-x-4">
                  <select
                    value={adminRoleFilter}
                    onChange={(e) => setAdminRoleFilter(e.target.value)}
                    className={`px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                    }`}
                  >
                    <option value="all">All Roles</option>
                    <option value="super-admin">Super Admin</option>
                    <option value="admin">Admin</option>
                    <option value="moderator">Moderator</option>
                  </select>
                  
                  <button
                    onClick={() => setShowAddAdmin(!showAddAdmin)}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center space-x-2"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Add Admin</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Add New Admin */}
            {showAddAdmin && (
              <div className={`p-6 rounded-lg shadow-sm ${
                darkMode ? 'bg-gray-800' : 'bg-white'
              }`}>
                <h3 className="text-md font-medium mb-4">Add New Admin User</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <input
                    type="text"
                    placeholder="Full Name *"
                    value={newAdmin.name}
                    onChange={(e) => setNewAdmin({...newAdmin, name: e.target.value})}
                    className={`px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                    }`}
                  />
                  
                  <input
                    type="email"
                    placeholder="Email Address *"
                    value={newAdmin.email}
                    onChange={(e) => setNewAdmin({...newAdmin, email: e.target.value})}
                    className={`px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                    }`}
                  />
                  
                  <input
                    type="password"
                    placeholder="Password *"
                    value={newAdmin.password}
                    onChange={(e) => setNewAdmin({...newAdmin, password: e.target.value})}
                    className={`px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                    }`}
                  />
                  
                  <select
                    value={newAdmin.role}
                    onChange={(e) => setNewAdmin({...newAdmin, role: e.target.value})}
                    className={`px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                    }`}
                  >
                    <option value="admin">Admin</option>
                    <option value="super-admin">Super Admin</option>
                    <option value="moderator">Moderator</option>
                  </select>
                </div>
                
                <div className="flex space-x-2">
                  <button
                    onClick={handleAddAdmin}
                    disabled={adminLoading}
                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 disabled:opacity-50"
                  >
                    <Save className="w-4 h-4" />
                    <span>{adminLoading ? 'Adding...' : 'Add Admin'}</span>
                  </button>
                  
                  <button
                    onClick={() => {
                      setShowAddAdmin(false);
                      setNewAdmin({ name: '', email: '', password: '', role: 'admin' });
                    }}
                    className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Edit Admin Modal */}
            {showEditAdmin && editingAdmin && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className={`max-w-md w-full mx-4 p-6 rounded-lg shadow-xl ${
                  darkMode ? 'bg-gray-800' : 'bg-white'
                }`}>
                  <h3 className="text-lg font-medium mb-4">Edit Admin User</h3>
                  
                  <div className="space-y-4">
                    <input
                      type="text"
                      placeholder="Full Name *"
                      value={editingAdmin.name}
                      onChange={(e) => setEditingAdmin({...editingAdmin, name: e.target.value})}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                      }`}
                    />
                    
                    <input
                      type="email"
                      placeholder="Email Address *"
                      value={editingAdmin.email}
                      onChange={(e) => setEditingAdmin({...editingAdmin, email: e.target.value})}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                      }`}
                    />
                    
                    <input
                      type="password"
                      placeholder="New Password (leave empty to keep current)"
                      value={editingAdmin.password}
                      onChange={(e) => setEditingAdmin({...editingAdmin, password: e.target.value})}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                      }`}
                    />
                    
                    <select
                      value={editingAdmin.role}
                      onChange={(e) => setEditingAdmin({...editingAdmin, role: e.target.value})}
                      disabled={editingAdmin.id === currentAdmin?.id} // Can't change own role
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                      } ${editingAdmin.id === currentAdmin?.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <option value="admin">Admin</option>
                      <option value="super-admin">Super Admin</option>
                      <option value="moderator">Moderator</option>
                    </select>
                    
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="editAdminActive"
                        checked={editingAdmin.active}
                        onChange={(e) => setEditingAdmin({...editingAdmin, active: e.target.checked})}
                        disabled={editingAdmin.id === currentAdmin?.id} // Can't deactivate own account
                        className={editingAdmin.id === currentAdmin?.id ? 'opacity-50 cursor-not-allowed' : ''}
                      />
                      <label htmlFor="editAdminActive" className="text-sm">
                        Active Account
                        {editingAdmin.id === currentAdmin?.id && (
                          <span className="text-xs text-gray-500 ml-2">(Cannot modify own account)</span>
                        )}
                      </label>
                    </div>
                  </div>
                  
                  <div className="flex space-x-2 mt-6">
                    <button
                      onClick={handleUpdateAdmin}
                      disabled={adminLoading}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 disabled:opacity-50"
                    >
                      <Save className="w-4 h-4" />
                      <span>{adminLoading ? 'Updating...' : 'Update Admin'}</span>
                    </button>
                    
                    <button
                      onClick={handleCancelEditAdmin}
                      className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                                                      </div>
                </div>
              </div>
            )}

            {/* Admin Users List */}
            <div className={`p-6 rounded-lg shadow-sm ${
              darkMode ? 'bg-gray-800' : 'bg-white'
            }`}>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">
                  Admin Users ({filteredAdminUsers.length} of {adminUsers.length})
                </h3>
              </div>
              
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className={darkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Email</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Role</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Last Login</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Created</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${darkMode ? 'divide-gray-600' : 'divide-gray-200'}`}>
                    {filteredAdminUsers.map((admin) => (
                      <tr key={admin.id} className={admin.id === currentAdmin?.id ? 
                        (darkMode ? 'bg-blue-900/20' : 'bg-blue-50') : ''
                      }>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center text-white text-sm font-bold mr-3">
                              {admin.name ? admin.name.charAt(0).toUpperCase() : admin.email.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <div className="text-sm font-medium">
                                {admin.name}
                                {admin.id === currentAdmin?.id && (
                                  <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                    You
                                  </span>
                                )}
                              </div>
                              <div className="text-xs text-gray-500">
                                ID: {admin.id}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {admin.email}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                            admin.role === 'super-admin' 
                              ? 'bg-purple-100 text-purple-800'
                              : admin.role === 'admin'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {admin.role.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                            admin.active 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {admin.active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {admin.lastLogin ? (
                            <div>
                              <div>{new Date(admin.lastLogin).toLocaleDateString()}</div>
                              <div className="text-xs text-gray-500">
                                {new Date(admin.lastLogin).toLocaleTimeString()}
                              </div>
                            </div>
                          ) : (
                            <span className="text-gray-500">Never</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {admin.createdAt ? (
                            <div>
                              <div>{new Date(admin.createdAt).toLocaleDateString()}</div>
                              <div className="text-xs text-gray-500">
                                {new Date(admin.createdAt).toLocaleTimeString()}
                              </div>
                            </div>
                          ) : (
                            <span className="text-gray-500">Unknown</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleEditAdmin(admin)}
                              disabled={adminLoading}
                              className="px-3 py-1 bg-blue-100 text-blue-800 hover:bg-blue-200 rounded text-xs transition-colors disabled:opacity-50 flex items-center space-x-1"
                            >
                              <Edit3 className="w-3 h-3" />
                              <span>Edit</span>
                            </button>
                            
                            {admin.id !== currentAdmin?.id && (
                              <>
                                <button
                                  onClick={() => handleToggleAdminStatus(admin.id, admin.active)}
                                  disabled={adminLoading}
                                  className={`px-3 py-1 rounded text-xs transition-colors disabled:opacity-50 ${
                                    admin.active
                                      ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                                      : 'bg-green-100 text-green-800 hover:bg-green-200'
                                  }`}
                                >
                                  {admin.active ? 'Deactivate' : 'Activate'}
                                </button>
                                
                                <button
                                  onClick={() => handleDeleteAdmin(admin.id)}
                                  disabled={adminLoading}
                                  className="px-3 py-1 bg-red-100 text-red-800 hover:bg-red-200 rounded text-xs transition-colors disabled:opacity-50 flex items-center space-x-1"
                                >
                                  <Trash2 className="w-3 h-3" />
                                  <span>Delete</span>
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                
                {filteredAdminUsers.length === 0 && (
                  <div className="text-center py-8">
                    <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">
                      {adminSearchTerm || adminRoleFilter !== 'all' 
                        ? 'No admin users match your search criteria.' 
                        : 'No admin users found.'}
                    </p>
                    {(adminSearchTerm || adminRoleFilter !== 'all') && (
                      <button
                        onClick={() => {
                          setAdminSearchTerm('');
                          setAdminRoleFilter('all');
                        }}
                        className="mt-2 text-blue-600 hover:text-blue-800 text-sm"
                      >
                        Clear filters
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Reports Tab */}
        {activeTab === 'reports' && (
          <div className="space-y-6">
            {/* Reports Header */}
            <div className={`p-6 rounded-lg shadow-sm ${
              darkMode ? 'bg-gray-800' : 'bg-white'
            }`}>
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h2 className="text-lg font-semibold">System Reports Dashboard</h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Analytics and insights from system activity logs
                  </p>
                </div>
                <div className="flex items-center space-x-4">
                  <select
                    value={reportsDaysFilter}
                    onChange={(e) => setReportsDaysFilter(Number(e.target.value))}
                    className={`px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                    }`}
                  >
                    <option value={30}>Last 30 Days</option>
                    <option value={60}>Last 60 Days</option>
                    <option value={90}>Last 90 Days</option>
                  </select>
                  <button
                    onClick={handleDataMigration}
                    disabled={migrationLoading || reportsLoading}
                    className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 disabled:opacity-50"
                  >
                    <Database className="w-4 h-4" />
                    <span>{migrationLoading ? 'Migrating...' : 'Migrate Data'}</span>
                  </button>
                  <button
                    onClick={handleGenerateTestLogs}
                    disabled={testLogsLoading || reportsLoading}
                    className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 disabled:opacity-50"
                  >
                    <Sparkles className="w-4 h-4" />
                    <span>{testLogsLoading ? 'Generating...' : 'Test Logs'}</span>
                  </button>
                  <button
                    onClick={() => downloadReportsData(reportsDaysFilter)}
                    disabled={reportsLoading}
                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 disabled:opacity-50"
                  >
                    <Download className="w-4 h-4" />
                    <span>Export CSV</span>
                  </button>
                </div>
              </div>
            </div>

            {reportsLoading ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-3">Loading reports data...</span>
              </div>
            ) : (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className={`p-6 rounded-lg shadow-sm ${
                    darkMode ? 'bg-gray-800' : 'bg-white'
                  }`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-500">Total Events</p>
                        <p className="text-2xl font-bold">{eventLogsSummary.totalEvents || 0}</p>
                      </div>
                      <BarChart3 className="w-8 h-8 text-blue-600" />
                    </div>
                  </div>

                  <div className={`p-6 rounded-lg shadow-sm ${
                    darkMode ? 'bg-gray-800' : 'bg-white'
                  }`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-500">Event Types</p>
                        <p className="text-2xl font-bold">{Object.keys(eventLogsSummary.eventsByType || {}).length}</p>
                      </div>
                      <FileText className="w-8 h-8 text-green-600" />
                    </div>
                  </div>

                  <div className={`p-6 rounded-lg shadow-sm ${
                    darkMode ? 'bg-gray-800' : 'bg-white'
                  }`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-500">Active Users</p>
                        <p className="text-2xl font-bold">{Object.keys(eventLogsSummary.eventsByUser || {}).length}</p>
                      </div>
                      <Users className="w-8 h-8 text-purple-600" />
                    </div>
                  </div>

                  <div className={`p-6 rounded-lg shadow-sm ${
                    darkMode ? 'bg-gray-800' : 'bg-white'
                  }`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-500">Active Days</p>
                        <p className="text-2xl font-bold">{Object.keys(eventLogsSummary.eventsByDate || {}).length}</p>
                      </div>
                      <Clock className="w-8 h-8 text-orange-600" />
                    </div>
                  </div>
                </div>

                {/* Event Types Chart */}
                <div className={`p-6 rounded-lg shadow-sm ${
                  darkMode ? 'bg-gray-800' : 'bg-white'
                }`}>
                  <h3 className="text-lg font-semibold mb-4">Event Types Distribution</h3>
                  <div className="space-y-3">
                    {Object.entries(eventLogsSummary.eventsByType || {})
                      .sort(([,a], [,b]) => b - a)
                      .slice(0, 10)
                      .map(([type, count]) => {
                        const percentage = ((count / eventLogsSummary.totalEvents) * 100).toFixed(1);
                        return (
                          <div key={type} className="flex items-center justify-between">
                            <div className="flex items-center space-x-3 flex-1">
                              <span className="text-sm font-medium min-w-0 flex-1">{type}</span>
                              <span className="text-sm text-gray-500">{count} events</span>
                              <span className="text-sm text-gray-500">{percentage}%</span>
                            </div>
                            <div className="w-32 bg-gray-200 dark:bg-gray-700 rounded-full h-2 ml-4">
                              <div 
                                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                                style={{ width: `${percentage}%` }}
                              ></div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>

                {/* User Activity Chart */}
                <div className={`p-6 rounded-lg shadow-sm ${
                  darkMode ? 'bg-gray-800' : 'bg-white'
                }`}>
                  <h3 className="text-lg font-semibold mb-4">User Activity</h3>
                  <div className="space-y-3">
                    {Object.entries(eventLogsSummary.eventsByUser || {})
                      .sort(([,a], [,b]) => b - a)
                      .slice(0, 10)
                      .map(([user, count]) => {
                        const percentage = ((count / eventLogsSummary.totalEvents) * 100).toFixed(1);
                        return (
                          <div key={user} className="flex items-center justify-between">
                            <div className="flex items-center space-x-3 flex-1">
                              <span className="text-sm font-medium min-w-0 flex-1">{user}</span>
                              <span className="text-sm text-gray-500">{count} actions</span>
                              <span className="text-sm text-gray-500">{percentage}%</span>
                            </div>
                            <div className="w-32 bg-gray-200 dark:bg-gray-700 rounded-full h-2 ml-4">
                              <div 
                                className="bg-green-600 h-2 rounded-full transition-all duration-300"
                                style={{ width: `${percentage}%` }}
                              ></div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>

                {/* Recent Events */}
                <div className={`p-6 rounded-lg shadow-sm ${
                  darkMode ? 'bg-gray-800' : 'bg-white'
                }`}>
                  <h3 className="text-lg font-semibold mb-4">Recent Events</h3>
                  <div className="space-y-3">
                    {(eventLogsSummary.recentEvents || []).map((event, index) => (
                      <div key={index} className={`p-3 rounded-lg border ${
                        darkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-200 bg-gray-50'
                      }`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                              (event.type || event.eventType)?.includes('ERROR') ? 'bg-red-100 text-red-800' :
                              (event.type || event.eventType)?.includes('DELETE') ? 'bg-orange-100 text-orange-800' :
                              (event.type || event.eventType)?.includes('CREATE') || (event.type || event.eventType)?.includes('ADD') ? 'bg-green-100 text-green-800' :
                              'bg-blue-100 text-blue-800'
                            }`}>
                              {event.type || event.eventType}
                            </span>
                            <span className="text-sm">{event.details}</span>
                          </div>
                          <div className="text-xs text-gray-500">
                            {new Date(event.timestamp).toLocaleString()}
                          </div>
                        </div>
                        <div className="mt-2 text-xs text-gray-500">
                          User: {event.userEmail || 'System'}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Event Logs Tab */}
        {activeTab === 'logs' && (
          <div className="space-y-6">
            {/* Event Logs Header */}
            <div className={`p-6 rounded-lg shadow-sm ${
              darkMode ? 'bg-gray-800' : 'bg-white'
            }`}>
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h2 className="text-lg font-semibold">Event Logs</h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Detailed system activity logs for troubleshooting and auditing
                  </p>
                </div>
                <div className="flex items-center space-x-4">
                  <select
                    value={logsDaysFilter}
                    onChange={(e) => setLogsDaysFilter(Number(e.target.value))}
                    className={`px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300'
                    }`}
                  >
                    <option value={30}>Last 30 Days</option>
                    <option value={60}>Last 60 Days</option>
                    <option value={90}>Last 90 Days</option>
                  </select>
                  <button
                    onClick={() => downloadEventLogs(logsDaysFilter)}
                    disabled={logsLoading}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 disabled:opacity-50"
                  >
                    <Download className="w-4 h-4" />
                    <span>Download Logs</span>
                  </button>
                  <button
                    onClick={handleDeleteOldLogs}
                    disabled={logsLoading}
                    className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 disabled:opacity-50"
                  >
                    <Trash2 className="w-4 h-4" />
                    <span>Cleanup Old Logs</span>
                  </button>
                </div>
              </div>
              
              <div className="text-sm text-gray-600 dark:text-gray-400">
                <p>• Logs are automatically retained for 90 days</p>
                <p>• Logs older than 90 days can be manually deleted</p>
                <p>• Logs less than 90 days old cannot be deleted</p>
                <p>• Only Super Admins can view and manage event logs</p>
              </div>
            </div>

            {logsLoading ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-3">Loading event logs...</span>
              </div>
            ) : (
              <div className={`p-6 rounded-lg shadow-sm ${
                darkMode ? 'bg-gray-800' : 'bg-white'
              }`}>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold">
                    Event Logs ({eventLogs.length} entries)
                  </h3>
                </div>
                
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className={darkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Timestamp</th>
                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Event Type</th>
                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">User</th>
                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Details</th>
                        <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">IP Address</th>
                      </tr>
                    </thead>
                    <tbody className={`divide-y ${darkMode ? 'divide-gray-600' : 'divide-gray-200'}`}>
                      {eventLogs.map((log) => (
                        <tr key={log.id}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <div>
                              <div>{new Date(log.timestamp).toLocaleDateString()}</div>
                              <div className="text-xs text-gray-500">
                                {new Date(log.timestamp).toLocaleTimeString()}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs rounded-full font-medium ${
                              (log.type || log.eventType)?.includes('ERROR') ? 'bg-red-100 text-red-800' :
                              (log.type || log.eventType)?.includes('DELETE') ? 'bg-orange-100 text-orange-800' :
                              (log.type || log.eventType)?.includes('CREATE') || (log.type || log.eventType)?.includes('ADD') ? 'bg-green-100 text-green-800' :
                              (log.type || log.eventType)?.includes('LOGIN') ? 'bg-blue-100 text-blue-800' :
                              (log.type || log.eventType)?.includes('LOGOUT') ? 'bg-gray-100 text-gray-800' :
                              'bg-purple-100 text-purple-800'
                            }`}>
                              {log.type || log.eventType}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <div>
                              <div>{log.userEmail || 'System'}</div>
                              {log.userId && (
                                <div className="text-xs text-gray-500">
                                  ID: {log.userId}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm">
                            <div className="max-w-xs truncate" title={log.details}>
                              {log.details}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {log.ipAddress || 'N/A'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  
                  {eventLogs.length === 0 && (
                    <div className="text-center py-8">
                      <Clock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-500">No event logs found for the selected time period.</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default AdminApp; 
